/* eslint-disable react/no-unescaped-entities, react-hooks/exhaustive-deps */
// Pre-existing patterns in this 5.7k-line file (ref mutations, try/catch
// in handlers, quotes in JSX strings). Suppressing globally until a
// scheduled refactor sprint splits this monolith.
import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Plus, Heart, Zap, TrendingUp, TrendingDown, Sparkles, RefreshCw, UserMinus, ThumbsUp, ThumbsDown, Pencil, Check, Target, Trash2, AlertTriangle, Copy, Brain, Lightbulb, Clock, Archive, RotateCcw, Eye, FileText, BarChart2, Download, FileSpreadsheet, Trophy, Medal, Crown, Swords, Edit, Users, X, Shield, Coins, MoreVertical, Minus, CheckSquare, FlaskConical, Upload, Link2, Image, Award, ExternalLink, Unlock, Send, UserPlus, Ban, BookOpen, Search } from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel } from '@/components/ui/dropdown-menu';
import { classroomAPI, studentAPI, behaviorAPI, gifAPI } from '../utils/api';
import { calculateXPNeeded, getMonsterGif, getMonsterGifByMonster, getMonsterFallbackGif, getClassBadge, getClassName, getEggGif, loadUploadedGifs, subscribeToCacheUpdates, getMonsterDisplayName, isoToLocalInputValue, nowLocalInputValue } from '../utils/helpers';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import TeacherNotificationBell from '../components/TeacherNotificationBell';
import { PokemonStatBars } from '../components/PokemonStatBars';
import { useWebSocket } from '../hooks/useWebSocket';
import { useAuth } from '../contexts/AuthContext';
import AIMissionGeneratorModal from '../components/AIMissionGeneratorModal';
import PokemonImage from '../components/PokemonImage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to build correct GIF URL (handles both Cloudinary and local)
const buildGifUrl = (gifUrl) => {
  if (!gifUrl) return '';
  // If it's already an absolute URL (Cloudinary), return as-is
  if (gifUrl.startsWith('http://') || gifUrl.startsWith('https://')) {
    return gifUrl;
  }
  // Otherwise, it's a local path, prepend backend URL
  return `${BACKEND_URL}${gifUrl}`;
};

// Debounce delay for API calls (ms)
const API_DEBOUNCE_DELAY = 150;

const TeacherClassroom = () => {
  const { classroomId } = useParams();
  const navigate = useNavigate();
  
  // Refs for debounce management
  const pendingUpdates = useRef(new Map());
  const debounceTimers = useRef(new Map());
  const [classroom, setClassroom] = useState(null);
  const [students, setStudents] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gifCacheReady, setGifCacheReady] = useState(false); // Track GIF cache state
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskForm, setTaskForm] = useState({ title: '', description: '', xp_reward: 100 });
  const [behaviors, setBehaviors] = useState({ positive: {}, negative: {} });
  const [behaviorDialogOpen, setBehaviorDialogOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedBehaviorType, setSelectedBehaviorType] = useState('positive');
  const [pokemonDialogOpen, setPokemonDialogOpen] = useState(false);
  const [selectedStudentForPokemon, setSelectedStudentForPokemon] = useState(null);
  const [availableGifs, setAvailableGifs] = useState({});
  const [loadingGifs, setLoadingGifs] = useState(false);
  const [eggGif, setEggGif] = useState(null);
  const [editingMonsterName, setEditingMonsterName] = useState(null);
  
  // Bulk XP states
  const [bulkXPDialogOpen, setBulkXPDialogOpen] = useState(false);
  const [selectedStudentsForXP, setSelectedStudentsForXP] = useState(new Set());
  const [bulkXPAmount, setBulkXPAmount] = useState('');
  const [bulkXPReason, setBulkXPReason] = useState('');
  const [bulkXPSearchQuery, setBulkXPSearchQuery] = useState('');
  const [isApplyingBulkXP, setIsApplyingBulkXP] = useState(false);
  
  // Group management states
  const [groups, setGroups] = useState([]);
  const [createGroupDialogOpen, setCreateGroupDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupColor, setNewGroupColor] = useState('#6366f1');
  const [groupXPDialogOpen, setGroupXPDialogOpen] = useState(false);
  const [selectedGroupForXP, setSelectedGroupForXP] = useState(null);
  const [groupXPAmount, setGroupXPAmount] = useState('');
  const [groupXPReason, setGroupXPReason] = useState('');
  const [isApplyingGroupXP, setIsApplyingGroupXP] = useState(false);
  const [draggedStudent, setDraggedStudent] = useState(null);
  const [tempMonsterName, setTempMonsterName] = useState('');
  
  // Card selection states (Redesign)
  const [selectedStudents, setSelectedStudents] = useState(new Set());
  const [bulkHPDialogOpen, setBulkHPDialogOpen] = useState(false);
  const [bulkHPAmount, setBulkHPAmount] = useState('');
  const [bulkCoinsDialogOpen, setBulkCoinsDialogOpen] = useState(false);
  const [bulkCoinsAmount, setBulkCoinsAmount] = useState('');
  // Mission states
  const [missions, setMissions] = useState([]);
  const [missionDialogOpen, setMissionDialogOpen] = useState(false);
  const [missionForm, setMissionForm] = useState({
    title: '',
    description: '',
    expires_at: '',
    coins_per_question: 5,
    perfect_bonus_pct: 50,
    lab_references: [],
    questions: [
      { question: '', question_type: 'multiple_choice', option_a: '', option_b: '', option_c: '', option_d: '', correct_answer: 'A', xp_value: 10, hint: '', key_concepts: [] }
    ]
  });
  const [deleteMissionDialogOpen, setDeleteMissionDialogOpen] = useState(false);
  const [deletingMission, setDeletingMission] = useState(null);
  const [archivedMissionsOpen, setArchivedMissionsOpen] = useState(false);  // Show archived missions tab
  
  // Edit Mission states
  const [editMissionDialogOpen, setEditMissionDialogOpen] = useState(false);
  const [editingMission, setEditingMission] = useState(null);
  const [completionsWarning, setCompletionsWarning] = useState(0);
  const [savingMission, setSavingMission] = useState(false);
  const [recalculateResults, setRecalculateResults] = useState(null);
  
  // View Mission states
  const [viewMissionDialogOpen, setViewMissionDialogOpen] = useState(false);
  const [viewingMission, setViewingMission] = useState(null);
  
  // Mission Report states
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [missionReport, setMissionReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportFilter, setReportFilter] = useState('all'); // 'all', 'completed', 'pending'
  
  // Student Answers states (view answers in report)
  const [studentAnswersDialogOpen, setStudentAnswersDialogOpen] = useState(false);
  const [studentAnswersData, setStudentAnswersData] = useState(null);
  const [loadingStudentAnswers, setLoadingStudentAnswers] = useState(false);
  const [answersFilter, setAnswersFilter] = useState('all'); // 'all', 'correct', 'wrong'
  
  // Unarchive Dialog states
  const [unarchiveDialogOpen, setUnarchiveDialogOpen] = useState(false);

  // Essay Correction states
  const [essayDialogOpen, setEssayDialogOpen] = useState(false);
  const [essaySubmissions, setEssaySubmissions] = useState(null);
  const [essaySummary, setEssaySummary] = useState(null);
  const [loadingEssays, setLoadingEssays] = useState(false);
  const [gradingEssay, setGradingEssay] = useState(null); // submission being graded
  const [gradeEdits, setGradeEdits] = useState({}); // {question_index: {score, feedback}}
  const [submittingGrade, setSubmittingGrade] = useState(false);
  const [unarchivingMission, setUnarchivingMission] = useState(null);
  
  // AI Mission Generator state
  const [aiGeneratorOpen, setAiGeneratorOpen] = useState(false);
  
  // Remove Student states
  const [removeStudentDialogOpen, setRemoveStudentDialogOpen] = useState(false);
  const [studentToRemove, setStudentToRemove] = useState(null);
  const [removingStudentId, setRemovingStudentId] = useState(null); // For fade-out animation
  const [isRemovingStudent, setIsRemovingStudent] = useState(false);

  // Transfer Student states
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [studentToTransfer, setStudentToTransfer] = useState(null);
  const [transferTargetClassroomId, setTransferTargetClassroomId] = useState('');
  const [isTransferringStudent, setIsTransferringStudent] = useState(false);
  const [transferError, setTransferError] = useState('');

  // Restore Student states
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [inactiveStudents, setInactiveStudents] = useState([]);
  const [loadingInactive, setLoadingInactive] = useState(false);
  const [restoreTargets, setRestoreTargets] = useState({}); // {student_id: classroom_id}
  const [restoringStudentId, setRestoringStudentId] = useState(null);

  // Bulk Transfer states
  const [bulkTransferDialogOpen, setBulkTransferDialogOpen] = useState(false);
  const [bulkTransferTargetId, setBulkTransferTargetId] = useState('');
  const [isBulkTransferring, setIsBulkTransferring] = useState(false);
  const [bulkTransferResult, setBulkTransferResult] = useState(null);

  // Clone Mission states
  const [cloneMissionDialogOpen, setCloneMissionDialogOpen] = useState(false);
  const [missionToClone, setMissionToClone] = useState(null);
  const [teacherClassrooms, setTeacherClassrooms] = useState([]);
  const [selectedClassroomsForClone, setSelectedClassroomsForClone] = useState([]);
  const [isCloningMission, setIsCloningMission] = useState(false);

  // Lab Research states
  const [labMaterials, setLabMaterials] = useState([]);
  const [labAnalytics, setLabAnalytics] = useState(null);
  const [labUploading, setLabUploading] = useState(false);
  const [labUploadForm, setLabUploadForm] = useState({ title: '', area: 'Geral', material_type: 'file', link_url: '' });
  const [labFile, setLabFile] = useState(null);
  const [labRewardCoins, setLabRewardCoins] = useState(10);
  const [labRewarding, setLabRewarding] = useState(false);

  // Get auth context for teacher ID
  const { user } = useAuth();

  // Memoized sorted students list (A-Z by name)
  const sortedStudents = useMemo(() => {
    return [...students].sort((a, b) => {
      const nameA = (a.user_name || '').toLowerCase();
      const nameB = (b.user_name || '').toLowerCase();
      return nameA.localeCompare(nameB, 'pt-BR');
    });
  }, [students]);

  // Leaderboard - Top 5 students by Level then XP
  const leaderboard = useMemo(() => {
    return [...students]
      .sort((a, b) => {
        // Primeiro por nível (maior primeiro)
        const levelDiff = (b.level || 1) - (a.level || 1);
        if (levelDiff !== 0) return levelDiff;
        // Depois por XP (maior primeiro)
        return (b.xp || 0) - (a.xp || 0);
      })
      .slice(0, 5)
      .map((student, index) => ({
        ...student,
        rank: index + 1
      }));
  }, [students]);

  // Card selection handlers
  const toggleStudentSelection = useCallback((studentId) => {
    setSelectedStudents(prev => {
      const next = new Set(prev);
      if (next.has(studentId)) next.delete(studentId);
      else next.add(studentId);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    const hatchedStudents = students.filter(s => s.egg_hatched);
    if (selectedStudents.size === hatchedStudents.length) {
      setSelectedStudents(new Set());
    } else {
      setSelectedStudents(new Set(hatchedStudents.map(s => s.student_id)));
    }
  }, [students, selectedStudents.size]);

  const clearSelection = useCallback(() => {
    setSelectedStudents(new Set());
  }, []);

  // Handle AI-generated mission
  const handleAIMissionGenerated = (aiMission) => {
    // Convert AI-generated mission to missionForm format
    const convertedQuestions = aiMission.questions.map(q => {
      const isEssay = q.question_type === 'essay';
      const alts = q.alternatives || [];
      return {
        question: q.question_text,
        question_type: isEssay ? 'essay' : 'multiple_choice',
        option_a: isEssay ? '' : (alts[0] || ''),
        option_b: isEssay ? '' : (alts[1] || ''),
        option_c: isEssay ? '' : (alts[2] || ''),
        option_d: isEssay ? '' : (alts[3] || ''),
        correct_answer: isEssay ? '' : (q.correct_answer || 'A'),
        key_concepts: isEssay ? (q.key_concepts || []) : [],
        bloom_level: q.bloom_level || null,
        xp_value: q.xp_value,
        hint: q.hint || ''
      };
    });

    setMissionForm({
      title: aiMission.title,
      description: aiMission.description,
      expires_at: '',
      coins_per_question: 5,
      perfect_bonus_pct: 50,
      questions: convertedQuestions
    });

    setAiGeneratorOpen(false);
    setMissionDialogOpen(true);
    toast.success('Missão gerada pela IA! Revise e publique quando estiver pronto.');
  };

  // WebSocket handler for real-time streak updates
  const handleTeacherWebSocketMessage = useCallback((message) => {
    console.log('[TeacherClassroom] WebSocket message:', message);
    
    if (message.type === 'student_streak_update') {
      const { student_id, win_streak, student_name } = message.data;
      
      // Update student's win_streak in local state
      setStudents(prev => prev.map(s => 
        s.student_id === student_id 
          ? { ...s, win_streak } 
          : s
      ));
      
      // Show toast for new streak
      if (win_streak >= 2) {
        toast.success(`🔥 ${student_name} está em combo x${win_streak}!`, { duration: 3000 });
      }
    }
  }, []);

  // Connect to WebSocket for real-time updates
  const { isConnected: wsConnected } = useWebSocket(
    'teacher',
    user?.user_id,
    handleTeacherWebSocketMessage
  );

  useEffect(() => {
    loadData();
    loadBehaviors();
    // Load GIF cache and set ready state
    loadUploadedGifs().then(() => {
      setEggGif(getEggGif());
      setGifCacheReady(true);
    });
    // Subscribe to GIF cache updates for reactive re-renders
    const unsubscribe = subscribeToCacheUpdates(() => {
      setGifCacheReady(true);
      setEggGif(getEggGif());
    });
    // Cleanup debounce timers on unmount
    return () => {
      debounceTimers.current.forEach(timer => clearTimeout(timer));
      unsubscribe();
    };
  }, [classroomId]);

  // Unified optimistic update helper - atualiza estado local imediatamente
  const updateStudentOptimistic = useCallback((studentId, updates) => {
    setStudents(prev => prev.map(s => 
      s.student_id === studentId ? { ...s, ...updates } : s
    ));
  }, []);

  // Debounced API call helper - agrupa chamadas rápidas
  const debouncedAPICall = useCallback((key, apiCall, onSuccess, onError) => {
    // Cancel existing timer for this key
    if (debounceTimers.current.has(key)) {
      clearTimeout(debounceTimers.current.get(key));
    }
    
    // Store the pending call
    pendingUpdates.current.set(key, { apiCall, onSuccess, onError });
    
    // Set new debounced timer
    const timer = setTimeout(async () => {
      const pending = pendingUpdates.current.get(key);
      if (pending) {
        try {
          const response = await pending.apiCall();
          pending.onSuccess?.(response);
        } catch (error) {
          pending.onError?.(error);
        }
        pendingUpdates.current.delete(key);
      }
      debounceTimers.current.delete(key);
    }, API_DEBOUNCE_DELAY);
    
    debounceTimers.current.set(key, timer);
  }, []);

  const loadData = async () => {
    try {
      const [classroomRes, studentsRes, tasksRes, missionsRes] = await Promise.all([
        classroomAPI.getById(classroomId),
        classroomAPI.getStudents(classroomId),
        classroomAPI.getTasks(classroomId),
        classroomAPI.getMissions(classroomId)
      ]);
      setClassroom(classroomRes.data);
      setStudents(studentsRes.data);
      setTasks(tasksRes.data);
      setMissions(missionsRes.data);
      
      // Load groups separately to avoid body-stream issues
      try {
        const groupsRes = await fetch(`${BACKEND_URL}/api/classrooms/${classroomId}/groups`, { credentials: 'include' });
        if (groupsRes.ok) {
          const groupsData = await groupsRes.json();
          setGroups(groupsData || []);
        }
      } catch (groupErr) {
        console.error('Failed to load groups:', groupErr);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const loadBehaviors = async () => {
    try {
      const response = await behaviorAPI.getTemplates();
      setBehaviors(response.data);
    } catch (error) {
      console.error('Failed to load behaviors:', error);
    }
  };

  const openBehaviorDialog = (student, behaviorType) => {
    setSelectedStudent(student);
    setSelectedBehaviorType(behaviorType);
    setBehaviorDialogOpen(true);
  };

  const handleApplyBehavior = async (behaviorId) => {
    if (!selectedStudent) return;
    
    // Encontra o comportamento selecionado
    const behavior = behaviors[selectedBehaviorType]?.[Object.keys(behaviors[selectedBehaviorType]).find(
      key => behaviors[selectedBehaviorType][key].behavior_id === behaviorId
    )];
    
    // Optimistic update - atualiza a UI imediatamente
    if (behavior) {
      setStudents(prevStudents => {
        const newStudents = prevStudents.map(student => {
          if (student.student_id === selectedStudent.student_id) {
            if (selectedBehaviorType === 'positive') {
              let newXp = student.xp + (behavior.xp_reward || 0);
              let newLevel = student.level;
              let xpNeeded = calculateXPNeeded(newLevel);
              while (newXp >= xpNeeded && newLevel < 50) {
                newXp -= xpNeeded;
                newLevel++;
                xpNeeded = calculateXPNeeded(newLevel);
              }
              return { ...student, xp: newXp, level: newLevel };
            } else {
              return { ...student, hp: Math.max(0, student.hp - (behavior.hp_penalty || 0)) };
            }
          }
          return student;
        });
        return newStudents;
      });
    }
    
    setBehaviorDialogOpen(false);
    const studentIdToUpdate = selectedStudent.student_id;
    
    try {
      const response = await behaviorAPI.applyBehavior(studentIdToUpdate, behaviorId);
      const data = response.data;
      
      // Atualiza com dados reais do servidor
      setStudents(prevStudents => 
        prevStudents.map(student => {
          if (student.student_id === studentIdToUpdate) {
            if (data.behavior_type === 'positive') {
              return { 
                ...student, 
                xp: data.new_xp, 
                level: data.new_level 
              };
            } else {
              return { ...student, hp: data.new_hp };
            }
          }
          return student;
        })
      );
      
      if (data.behavior_type === 'positive') {
        toast.success(`+${data.xp_awarded} AURA - ${data.behavior_name}`);
        if (data.level_up) {
          confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
        }
      } else {
        toast.error(`-${data.hp_lost} HP - ${data.behavior_name}`);
      }
    } catch (error) {
      console.error('Failed to apply behavior:', error);
      toast.error('Erro ao aplicar comportamento');
      loadData(); // Reverte em caso de erro
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      await classroomAPI.createTask(classroomId, taskForm);
      toast.success('Tarefa criada com sucesso!');
      setTaskDialogOpen(false);
      setTaskForm({ title: '', description: '', xp_reward: 100 });
      loadData();
    } catch (error) {
      console.error('Failed to create task:', error);
      toast.error('Erro ao criar tarefa');
    }
  };

  const handleApplyPenalty = useCallback((studentId, hpReduction) => {
    // 1. Optimistic update - atualiza UI imediatamente
    setStudents(prev => prev.map(s => 
      s.student_id === studentId 
        ? { ...s, hp: Math.max(0, s.hp - hpReduction) }
        : s
    ));
    
    // 2. Debounced API call
    const key = `penalty-${studentId}`;
    debouncedAPICall(
      key,
      () => studentAPI.applyPenalty(studentId, hpReduction),
      (response) => {
        // Sync with server response
        setStudents(prev => prev.map(s => 
          s.student_id === studentId ? { ...s, hp: response.data.new_hp } : s
        ));
        toast.error(`-${hpReduction} HP`);
      },
      (error) => {
        console.error('Failed to apply penalty:', error);
        toast.error('Erro ao aplicar penalidade');
        loadData();
      }
    );
  }, [debouncedAPICall]);

  const handleHealStudent = useCallback((studentId, hpAmount) => {
    // 1. Optimistic update
    setStudents(prev => prev.map(s => {
      if (s.student_id === studentId) {
        const maxHp = s.max_hp || 100;
        return { ...s, hp: Math.min(maxHp, s.hp + hpAmount) };
      }
      return s;
    }));
    
    // 2. Debounced API call
    const key = `heal-${studentId}`;
    debouncedAPICall(
      key,
      () => studentAPI.healStudent(studentId, hpAmount),
      (response) => {
        setStudents(prev => prev.map(s => 
          s.student_id === studentId ? { ...s, hp: response.data.new_hp } : s
        ));
        toast.success(`+${hpAmount} HP 💚`);
      },
      (error) => {
        console.error('Failed to heal student:', error);
        toast.error('Erro ao curar');
        loadData();
      }
    );
  }, [debouncedAPICall]);

  const handleRechargeEnergy = useCallback((studentId, energyAmount) => {
    // 1. Optimistic update
    setStudents(prev => prev.map(s => {
      if (s.student_id === studentId) {
        const maxEnergy = s.max_energy || 50;
        return { ...s, energy: Math.min(maxEnergy, s.energy + energyAmount) };
      }
      return s;
    }));
    
    // 2. Debounced API call
    const key = `energy-${studentId}`;
    debouncedAPICall(
      key,
      () => studentAPI.rechargeEnergy(studentId, energyAmount),
      (response) => {
        setStudents(prev => prev.map(s => 
          s.student_id === studentId ? { ...s, energy: response.data.new_energy } : s
        ));
        toast.success(`+${energyAmount} Energia ⚡`);
      },
      (error) => {
        console.error('Failed to recharge energy:', error);
        toast.error('Erro ao recarregar energia');
        loadData();
      }
    );
  }, [debouncedAPICall]);

  // Bulk HP handler (for floating bar)
  const handleBulkHP = useCallback(async (amount) => {
    if (selectedStudents.size === 0) return;
    const ids = Array.from(selectedStudents);
    let successCount = 0;
    for (const sid of ids) {
      try {
        if (amount > 0) {
          handleHealStudent(sid, amount);
        } else {
          handleApplyPenalty(sid, Math.abs(amount));
        }
        successCount++;
      } catch (e) { console.error('Bulk HP error:', e); }
    }
    toast.success(`${amount > 0 ? '+' : ''}${amount} HP aplicado a ${successCount} aluno${successCount !== 1 ? 's' : ''}!`);
    clearSelection();
  }, [selectedStudents, handleHealStudent, handleApplyPenalty, clearSelection]);

  // Bulk Energy handler (for floating bar)
  const handleBulkEnergy = useCallback(async (amount) => {
    if (selectedStudents.size === 0) return;
    const ids = Array.from(selectedStudents);
    let successCount = 0;
    for (const sid of ids) {
      try {
        handleRechargeEnergy(sid, amount);
        successCount++;
      } catch (e) { console.error('Bulk Energy error:', e); }
    }
    toast.success(`+${amount} Energia aplicada a ${successCount} aluno${successCount !== 1 ? 's' : ''}!`);
    clearSelection();
  }, [selectedStudents, handleRechargeEnergy, clearSelection]);

  const handleAdjustXP = useCallback((studentId, xpChange) => {
    // 1. Optimistic update with level calculation
    let leveledUp = false;
    setStudents(prev => prev.map(s => {
      if (s.student_id === studentId) {
        let newXp = Math.max(0, s.xp + xpChange);
        let newLevel = s.level;
        
        // Calculate level up
        if (xpChange > 0) {
          let xpNeeded = calculateXPNeeded(newLevel);
          while (newXp >= xpNeeded && newLevel < 50) {
            newXp -= xpNeeded;
            newLevel++;
            xpNeeded = calculateXPNeeded(newLevel);
            leveledUp = true;
          }
        }
        
        return { ...s, xp: newXp, level: newLevel };
      }
      return s;
    }));
    
    // Show immediate feedback
    if (xpChange > 0) {
      toast.success(`+${xpChange} XP ⭐`);
    } else {
      toast.error(`${xpChange} XP`);
    }
    
    // 2. Debounced API call
    const key = `xp-${studentId}`;
    debouncedAPICall(
      key,
      () => studentAPI.adjustXP(studentId, xpChange),
      (response) => {
        // Sync with server and check for level up
        setStudents(prev => prev.map(s => {
          if (s.student_id === studentId) {
            const updates = { xp: response.data.new_xp, level: response.data.new_level };
            
            // Update monster stage if evolution occurred
            if (response.data.evolution && response.data.evolution.evolved && s.monster) {
              updates.monster = {
                ...s.monster,
                stage: response.data.evolution.to_stage
              };
            }
            
            return { ...s, ...updates };
          }
          return s;
        }));
        
        if (response.data.level_up) {
          confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
        }
        
        // Show evolution toast for teacher
        if (response.data.evolution && response.data.evolution.evolved) {
          setTimeout(() => {
            confetti({ 
              particleCount: 200, 
              spread: 100, 
              origin: { y: 0.5 },
              colors: ['#FFD700', '#FFA500', '#FF6347', '#9333EA']
            });
            toast.success(
              `🌟 ${response.data.evolution.monster_name} evoluiu para o estágio ${response.data.evolution.to_stage}!`,
              { duration: 5000 }
            );
          }, 300);
        }
      },
      (error) => {
        console.error('Failed to adjust XP:', error);
        toast.error('Erro ao ajustar AURA');
        loadData();
      }
    );
  }, [debouncedAPICall]);

  const handleAssignClass = async (studentId, classType) => {
    try {
      await studentAPI.assignClass(studentId, classType);
      toast.success('Classe atribuída!');
      loadData();
    } catch (error) {
      console.error('Failed to assign class:', error);
      toast.error('Erro ao atribuir classe');
    }
  };

  // ============ BULK XP FUNCTIONS ============
  const handleToggleStudentSelection = (studentId) => {
    setSelectedStudentsForXP(prev => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }
      return newSet;
    });
  };

  const handleToggleSelectAll = () => {
    if (selectedStudentsForXP.size === students.length) {
      setSelectedStudentsForXP(new Set());
    } else {
      setSelectedStudentsForXP(new Set(students.map(s => s.student_id)));
    }
  };

  const handleApplyBulkXP = async () => {
    if (selectedStudentsForXP.size === 0) {
      toast.error('Selecione pelo menos um aluno');
      return;
    }
    
    const xpAmount = parseInt(bulkXPAmount);
    if (isNaN(xpAmount) || xpAmount <= 0) {
      toast.error('Digite um valor de AURA válido (maior que 0)');
      return;
    }
    
    setIsApplyingBulkXP(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/students/bulk-xp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          student_ids: Array.from(selectedStudentsForXP),
          xp_amount: xpAmount,
          reason: bulkXPReason || null
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erro ao atribuir AURA');
      }
      
      const result = await response.json();
      
      // Show success message with confetti
      confetti({
        particleCount: 150,
        spread: 100,
        origin: { y: 0.6 }
      });
      
      toast.success(
        `Sucesso! ${xpAmount} AURA atribuída a ${result.successful} aluno${result.successful !== 1 ? 's' : ''}!`,
        {
          description: result.level_ups > 0 
            ? `🎉 ${result.level_ups} aluno${result.level_ups !== 1 ? 's' : ''} subiu de nível!`
            : undefined,
          duration: 5000
        }
      );
      
      // Reset form and close dialog
      setBulkXPDialogOpen(false);
      setSelectedStudentsForXP(new Set());
      setBulkXPAmount('');
      setBulkXPReason('');
      setBulkXPSearchQuery('');
      
      // Reload data to show updated XP/levels
      loadData();
      
    } catch (error) {
      console.error('Failed to apply bulk XP:', error);
      toast.error(error.message || 'Erro ao atribuir AURA');
    } finally {
      setIsApplyingBulkXP(false);
    }
  };

  // ============ GROUP MANAGEMENT FUNCTIONS ============
  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      toast.error('Digite o nome do grupo');
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/classrooms/${classroomId}/groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: newGroupName.trim(),
          color: newGroupColor
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erro ao criar grupo');
      }
      
      toast.success(`Grupo "${newGroupName}" criado!`);
      setCreateGroupDialogOpen(false);
      setNewGroupName('');
      setNewGroupColor('#6366f1');
      loadData();
    } catch (error) {
      console.error('Failed to create group:', error);
      toast.error(error.message || 'Erro ao criar grupo');
    }
  };

  const handleUpdateGroup = async () => {
    if (!editingGroup || !newGroupName.trim()) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/groups/${editingGroup.group_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: newGroupName.trim(),
          color: newGroupColor
        })
      });
      
      if (!response.ok) throw new Error('Erro ao atualizar grupo');
      
      toast.success('Grupo atualizado!');
      setEditingGroup(null);
      setCreateGroupDialogOpen(false);
      setNewGroupName('');
      setNewGroupColor('#6366f1');
      loadData();
    } catch (error) {
      console.error('Failed to update group:', error);
      toast.error('Erro ao atualizar grupo');
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!confirm('Tem certeza que deseja excluir este grupo? Os alunos serão desvinculados.')) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/groups/${groupId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Erro ao excluir grupo');
      
      toast.success('Grupo excluído!');
      loadData();
    } catch (error) {
      console.error('Failed to delete group:', error);
      toast.error('Erro ao excluir grupo');
    }
  };

  const handleAddStudentToGroup = async (groupId, studentId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/groups/${groupId}/members`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ student_id: studentId })
      });
      
      if (!response.ok) throw new Error('Erro ao adicionar aluno');
      
      toast.success('Aluno adicionado ao grupo!');
      loadData();
    } catch (error) {
      console.error('Failed to add student to group:', error);
      toast.error('Erro ao adicionar aluno');
    }
  };

  const handleRemoveStudentFromGroup = async (groupId, studentId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/groups/${groupId}/members/${studentId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Erro ao remover aluno');
      
      toast.success('Aluno removido do grupo!');
      loadData();
    } catch (error) {
      console.error('Failed to remove student from group:', error);
      toast.error('Erro ao remover aluno');
    }
  };

  const handleApplyGroupXP = async () => {
    if (!selectedGroupForXP) return;
    
    const xpAmount = parseInt(groupXPAmount);
    if (isNaN(xpAmount) || xpAmount <= 0) {
      toast.error('Digite um valor de AURA válido');
      return;
    }
    
    setIsApplyingGroupXP(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/groups/${selectedGroupForXP.group_id}/xp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          xp_amount: xpAmount,
          reason: groupXPReason || null
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erro ao atribuir AURA');
      }
      
      const result = await response.json();
      
      confetti({
        particleCount: 150,
        spread: 100,
        origin: { y: 0.6 }
      });
      
      toast.success(
        `${xpAmount} AURA atribuída ao grupo "${selectedGroupForXP.name}"!`,
        {
          description: result.level_ups > 0 
            ? `🎉 ${result.level_ups} aluno${result.level_ups !== 1 ? 's' : ''} subiu de nível!`
            : `${result.successful} membro${result.successful !== 1 ? 's' : ''} beneficiado${result.successful !== 1 ? 's' : ''}`,
          duration: 5000
        }
      );
      
      setGroupXPDialogOpen(false);
      setSelectedGroupForXP(null);
      setGroupXPAmount('');
      setGroupXPReason('');
      loadData();
    } catch (error) {
      console.error('Failed to apply group XP:', error);
      toast.error(error.message || 'Erro ao atribuir AURA ao grupo');
    } finally {
      setIsApplyingGroupXP(false);
    }
  };

  // Drag & Drop handlers
  const handleDragStart = (e, student) => {
    setDraggedStudent(student);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, groupId) => {
    e.preventDefault();
    if (draggedStudent) {
      await handleAddStudentToGroup(groupId, draggedStudent.student_id);
      setDraggedStudent(null);
    }
  };

  const handleHatchEgg = async (studentId) => {
    try {
      const response = await studentAPI.hatchEgg(studentId);
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
      toast.success(`Ovo chocado! ${response.data.monster.name} nasceu!`);
      loadData();
    } catch (error) {
      console.error('Failed to hatch egg:', error);
      toast.error(error.response?.data?.detail || 'Erro ao chocar ovo');
    }
  };

  const handleEvolveMonster = async (studentId) => {
    try {
      const response = await studentAPI.evolveMonster(studentId);
      // Numeric coercion guarantee: server returns ints, but be defensive
      // in case some legacy code path serialized them as strings.
      const evolved = response.data?.evolved !== false;

      if (!evolved) {
        // Idempotent no-op (already at correct stage for current level).
        // Don't show a scary toast — just inform calmly and refresh.
        toast.info(
          response.data?.message || 'Este Pokémon já está no estágio correto para o nível atual.'
        );
        loadData();
        return;
      }

      confetti({
        particleCount: 150,
        spread: 100,
        origin: { y: 0.6 }
      });
      const newName = response.data?.new_name || `estágio ${response.data?.new_stage}`;
      const oldName = response.data?.old_name;
      toast.success(
        oldName && newName
          ? `${oldName} evoluiu para ${newName}! 🌟`
          : `Monstro evoluiu para ${newName}!`
      );
      // Force fresh fetch so the cards re-evaluate the evolution tree
      // (sprites/names update via the new gif_resolver pipeline).
      loadData();
    } catch (error) {
      console.error('Failed to evolve monster:', error);
      toast.error(error.response?.data?.detail || 'Erro ao evoluir monstro');
    }
  };

  const handleResetEgg = async (studentId) => {
    if (!window.confirm('Tem certeza que deseja resetar o ovo deste aluno? Isso irá resetar TODO o progresso do monstro atual!')) {
      return;
    }

    try {
      await studentAPI.resetEgg(studentId);
      toast.success('Progresso resetado! O aluno pode receber um novo monstro.');
      loadData();
    } catch (error) {
      console.error('Failed to reset egg:', error);
      toast.error('Erro ao resetar');
    }
  };

  const openChangePokemonDialog = async (student) => {
    setSelectedStudentForPokemon(student);
    setPokemonDialogOpen(true);
    
    // Load available GIFs for this class
    if (student.monster?.class_type) {
      setLoadingGifs(true);
      try {
        const response = await gifAPI.getByClass(student.monster.class_type);
        setAvailableGifs(response.data);
      } catch (error) {
        console.error('Failed to load GIFs:', error);
        setAvailableGifs({});
      } finally {
        setLoadingGifs(false);
      }
    }
  };

  const handleChangePokemon = async (monsterIndex) => {
    if (!selectedStudentForPokemon) return;
    
    try {
      const response = await studentAPI.changePokemon(selectedStudentForPokemon.student_id, monsterIndex);
      const { old_name, new_name } = response.data;
      toast.success(`Pokemon trocado: ${old_name} → ${new_name}`, { duration: 4000 });
      setPokemonDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Failed to change pokemon:', error);
      toast.error('Erro ao trocar pokemon');
    }
  };

  const startEditingMonsterName = (studentId, currentName) => {
    setEditingMonsterName(studentId);
    setTempMonsterName(currentName || '');
  };

  const handleRenameMonster = async (studentId) => {
    if (!tempMonsterName.trim()) {
      setEditingMonsterName(null);
      return;
    }
    
    // Optimistic update
    setStudents(prevStudents => 
      prevStudents.map(student => {
        if (student.student_id === studentId && student.monster) {
          return { 
            ...student, 
            monster: { ...student.monster, name: tempMonsterName.trim() } 
          };
        }
        return student;
      })
    );
    
    setEditingMonsterName(null);
    
    try {
      await studentAPI.renameMonster(studentId, tempMonsterName.trim());
      toast.success('Nome do Pokémon atualizado!');
    } catch (error) {
      console.error('Failed to rename monster:', error);
      toast.error('Erro ao renomear Pokémon');
      loadData(); // Revert on error
    }
  };

  const handleMonsterNameKeyDown = (e, studentId) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleRenameMonster(studentId);
    } else if (e.key === 'Escape') {
      setEditingMonsterName(null);
    }
  };

  // Mission Functions
  const resetMissionForm = () => {
    setMissionForm({
      title: '',
      description: '',
      expires_at: '',
      coins_per_question: 5,
      perfect_bonus_pct: 50,
      lab_references: [],
      questions: [
        { question: '', question_type: 'multiple_choice', option_a: '', option_b: '', option_c: '', option_d: '', correct_answer: 'A', xp_value: 10, hint: '', key_concepts: [] }
      ]
    });
    loadLabMaterials();
  };

  const handleAddQuestion = () => {
    if (missionForm.questions.length >= 10) {
      toast.error('Máximo de 10 perguntas por missão');
      return;
    }
    setMissionForm(prev => ({
      ...prev,
      questions: [
        ...prev.questions,
        { question: '', question_type: 'multiple_choice', option_a: '', option_b: '', option_c: '', option_d: '', correct_answer: 'A', xp_value: 10, hint: '', key_concepts: [] }
      ]
    }));
  };

  const handleRemoveQuestion = (index) => {
    if (missionForm.questions.length <= 1) {
      toast.error('Necessário pelo menos 1 pergunta');
      return;
    }
    setMissionForm(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }));
  };

  const handleQuestionChange = (index, field, value) => {
    setMissionForm(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === index ? { ...q, [field]: value } : q
      )
    }));
  };

  const handleCreateMission = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!missionForm.title.trim()) {
      toast.error('Título é obrigatório');
      return;
    }

    if (!missionForm.expires_at) {
      toast.error('Defina o prazo de encerramento da missão (data e hora)');
      return;
    }
    if (new Date(missionForm.expires_at).getTime() <= Date.now()) {
      toast.error('O prazo da missão deve ser no futuro');
      return;
    }
    
    for (let i = 0; i < missionForm.questions.length; i++) {
      const q = missionForm.questions[i];
      if (!q.question.trim()) {
        toast.error(`Questão ${i + 1} está incompleta`);
        return;
      }
      if ((q.question_type || 'multiple_choice') === 'multiple_choice' && (!q.option_a?.trim() || !q.option_b?.trim())) {
        toast.error(`Questão objetiva ${i + 1}: preencha pelo menos alternativas A e B`);
        return;
      }
      if (q.question_type === 'essay' && (!q.key_concepts || q.key_concepts.length === 0)) {
        toast.error(`Questão dissertativa ${i + 1}: adicione pelo menos 1 conceito-chave`);
        return;
      }
    }
    
    try {
      await classroomAPI.createMission(classroomId, missionForm);
      toast.success('Missão criada com sucesso!');
      setMissionDialogOpen(false);
      resetMissionForm();
      loadData();
    } catch (error) {
      console.error('Failed to create mission:', error);
      const detail = error?.response?.data?.detail;
      toast.error(detail || 'Erro ao criar missão');
    }
  };

  const handleDeleteMission = async () => {
    if (!deletingMission) return;
    
    try {
      await classroomAPI.deleteMission(classroomId, deletingMission.mission_id);
      toast.success('Missão excluída');
      setDeleteMissionDialogOpen(false);
      setDeletingMission(null);
      loadData();
    } catch (error) {
      console.error('Failed to delete mission:', error);
      toast.error('Erro ao excluir missão');
    }
  };

  // Extend mission deadline
  const handleExtendMission = async (missionId, hours) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/missions/${missionId}/extend`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ hours })
      });
      
      if (!response.ok) throw new Error('Failed to extend');
      
      const data = await response.json();
      toast.success(data.message || `Missão prorrogada por ${hours}h!`, { icon: '⏰' });
      loadData();
    } catch (error) {
      console.error('Failed to extend mission:', error);
      toast.error('Erro ao prorrogar missão');
    }
  };

  // Archive mission
  const handleArchiveMission = async (missionId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/missions/${missionId}/archive`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to archive');
      
      toast.success('Missão arquivada!', { icon: '📦' });
      loadData();
    } catch (error) {
      console.error('Failed to archive mission:', error);
      toast.error('Erro ao arquivar missão');
    }
  };

  // Release Gabarito (Liberar Gabarito Agora)
  const handleReleaseGabarito = async (mission) => {
    const ok = window.confirm(
      `Liberar o gabarito de "${mission.title}" agora para toda a turma?\n\n` +
      'Os alunos verão imediatamente a correção detalhada (alternativas certas/erradas).\n' +
      'Esta ação não pode ser desfeita.'
    );
    if (!ok) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/missions/${mission.mission_id}/release-gabarito`, {
        method: 'POST',
        credentials: 'include'
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Erro ao liberar gabarito');
      }
      const data = await response.json();
      toast.success(data.message || 'Gabarito liberado!', { icon: '🔓' });
      loadData();
    } catch (error) {
      console.error('Failed to release gabarito:', error);
      toast.error(error.message || 'Erro ao liberar gabarito');
    }
  };

  // Close Mission (Encerrar Missão Agora)
  const handleCloseMission = async (mission) => {
    const ok = window.confirm(
      `Encerrar a missão "${mission.title}" agora?\n\n` +
      '• Os alunos não poderão mais enviar respostas.\n' +
      '• O gabarito será liberado imediatamente para a turma.\n\n' +
      'Esta ação não pode ser desfeita.'
    );
    if (!ok) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/missions/${mission.mission_id}/close`, {
        method: 'POST',
        credentials: 'include'
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Erro ao encerrar missão');
      }
      const data = await response.json();
      toast.success(data.message || 'Missão encerrada!', { icon: '🛑' });
      loadData();
    } catch (error) {
      console.error('Failed to close mission:', error);
      toast.error(error.message || 'Erro ao encerrar missão');
    }
  };

  // Open edit mission dialog
  const openEditMissionDialog = async (mission) => {
    // Deep copy the mission to avoid mutation issues
    const missionCopy = JSON.parse(JSON.stringify(mission));
    
    // Ensure all questions have required fields
    if (missionCopy.questions) {
      missionCopy.questions = missionCopy.questions.map(q => ({
        question: q.question || '',
        option_a: q.option_a || '',
        option_b: q.option_b || '',
        option_c: q.option_c || '',
        option_d: q.option_d || '',
        correct_answer: q.correct_answer || 'A',
        xp_value: q.xp_value || 10,
        hint: q.hint || ''
      }));
    } else {
      missionCopy.questions = [];
    }
    
    setEditingMission({
      ...missionCopy,
      expires_at: missionCopy.expires_at || ''
    });
    
    // Check how many students have completed this mission
    try {
      const response = await fetch(`${BACKEND_URL}/api/missions/${mission.mission_id}/check-completions`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCompletionsWarning(data.completions_count);
      }
    } catch (e) {
      console.error('Failed to check completions:', e);
    }
    
    setEditMissionDialogOpen(true);
  };

  // Save edited mission
  const handleSaveEditedMission = async () => {
    if (!editingMission) return;
    
    setSavingMission(true);
    setRecalculateResults(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/missions/${editingMission.mission_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: editingMission.title,
          description: editingMission.description,
          questions: editingMission.questions,
          expires_at: editingMission.expires_at || null,
          recalculate_completions: completionsWarning > 0  // Signal to recalculate if there are completions
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update');
      }
      
      const data = await response.json();
      toast.success(data.message || 'Missão atualizada!');
      
      // Show recalculation results if any
      if (data.recalculation_results) {
        const results = data.recalculation_results;
        if (results.affected_students > 0) {
          toast.info(`${results.affected_students} aluno(s) tiveram suas notas recalculadas!`, { 
            duration: 5000,
            icon: '📊'
          });
          setRecalculateResults(results);
        }
      }
      
      if (data.warning) {
        toast.warning(data.warning, { duration: 5000 });
      }
      
      setEditMissionDialogOpen(false);
      setEditingMission(null);
      setCompletionsWarning(0);
      loadData();
    } catch (error) {
      console.error('Failed to edit mission:', error);
      toast.error(error.message || 'Erro ao atualizar missão');
    } finally {
      setSavingMission(false);
    }
  };

  // Open mission report
  const openMissionReport = async (mission) => {
    setLoadingReport(true);
    setReportDialogOpen(true);
    setReportFilter('all'); // Reset filter when opening
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/missions/${mission.mission_id}/report`, {
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to load report');
      
      const data = await response.json();
      setMissionReport(data);
    } catch (error) {
      console.error('Failed to load report:', error);
      toast.error('Erro ao carregar relatório');
      setReportDialogOpen(false);
    } finally {
      setLoadingReport(false);
    }
  };

  // View student's answers in mission report
  const openStudentAnswers = async (missionId, studentId, studentName) => {
    setLoadingStudentAnswers(true);
    setStudentAnswersDialogOpen(true);
    setAnswersFilter('all'); // Reset filter when opening
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/missions/${missionId}/student/${studentId}/answers`, {
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Failed to load student answers');
      
      const data = await response.json();
      setStudentAnswersData(data);
    } catch (error) {
      console.error('Failed to load student answers:', error);
      toast.error('Erro ao carregar respostas do aluno');
      setStudentAnswersDialogOpen(false);
    } finally {
      setLoadingStudentAnswers(false);
    }
  };

  // Filter helpers for report
  const getFilteredStudents = () => {
    if (!missionReport?.students) return [];
    switch (reportFilter) {
      case 'completed':
        return missionReport.students.filter(s => s.status === 'completed');
      case 'pending':
        return missionReport.students.filter(s => s.status === 'pending');
      default:
        return missionReport.students;
    }
  };

  // Filter helpers for student answers
  const getFilteredAnswers = () => {
    if (!studentAnswersData?.questions) return [];
    switch (answersFilter) {
      case 'correct':
        return studentAnswersData.questions.filter(q => q.is_correct);
      case 'wrong':
        return studentAnswersData.questions.filter(q => !q.is_correct);
      default:
        return studentAnswersData.questions;
    }
  };

  // Export report to CSV
  const exportReportToCSV = () => {
    if (!missionReport) return;
    
    const headers = ['Aluno', 'Email', 'Status', 'Acertos', 'Total', 'Porcentagem', 'AURA Ganha'];
    const rows = missionReport.students.map(student => [
      student.name,
      student.email || '',
      student.status === 'completed' ? 'Concluída' : 'Pendente',
      student.status === 'completed' ? student.correct_count : '',
      student.status === 'completed' ? student.total_questions : '',
      student.status === 'completed' ? `${student.percentage}%` : '',
      student.status === 'completed' ? student.xp_earned : ''
    ]);
    
    // Add summary row
    rows.push([]);
    rows.push(['--- RESUMO ---']);
    rows.push(['Total de Alunos', missionReport.total_students]);
    rows.push(['Concluídas', missionReport.completed_count]);
    rows.push(['Pendentes', missionReport.pending_count]);
    rows.push(['Média da Turma', `${missionReport.average_score}%`]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `relatorio_${missionReport.mission_title.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast.success('Relatório exportado para CSV!', { icon: '📊' });
  };

  // Export report to PDF (simple HTML-based approach)
  const exportReportToPDF = () => {
    if (!missionReport) return;
    
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Pop-up bloqueado. Permita pop-ups para exportar PDF.');
      return;
    }
    
    const completedStudents = missionReport.students.filter(s => s.status === 'completed');
    const pendingStudents = missionReport.students.filter(s => s.status === 'pending');
    
    // Calculate score ranges for histogram
    const ranges = [
      { label: '0-49%', min: 0, max: 49, color: '#ef4444', count: 0 },
      { label: '50-69%', min: 50, max: 69, color: '#f97316', count: 0 },
      { label: '70-89%', min: 70, max: 89, color: '#eab308', count: 0 },
      { label: '90-100%', min: 90, max: 100, color: '#22c55e', count: 0 }
    ];
    completedStudents.forEach(s => {
      const range = ranges.find(r => s.percentage >= r.min && s.percentage <= r.max);
      if (range) range.count++;
    });
    const maxCount = Math.max(...ranges.map(r => r.count), 1);
    
    // Calculate completion percentage for donut chart
    const completionPercent = missionReport.total_students > 0 
      ? Math.round((missionReport.completed_count / missionReport.total_students) * 100) 
      : 0;
    const strokeDasharray = (missionReport.completed_count / missionReport.total_students) * 251.2;
    
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Relatório - ${missionReport.mission_title}</title>
        <meta charset="UTF-8">
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; color: #1e293b; }
          .header { text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #e2e8f0; }
          .header h1 { color: #3b82f6; font-size: 24px; margin-bottom: 8px; }
          .header h2 { color: #64748b; font-size: 16px; font-weight: normal; }
          .header .date { color: #94a3b8; font-size: 12px; margin-top: 10px; }
          .summary { display: flex; justify-content: space-around; margin: 30px 0; }
          .summary-card { text-align: center; padding: 20px 30px; border-radius: 12px; }
          .summary-card.green { background: #dcfce7; }
          .summary-card.orange { background: #ffedd5; }
          .summary-card.purple { background: #f3e8ff; }
          .summary-card .value { font-size: 32px; font-weight: bold; }
          .summary-card.green .value { color: #16a34a; }
          .summary-card.orange .value { color: #ea580c; }
          .summary-card.purple .value { color: #9333ea; }
          .summary-card .label { font-size: 14px; color: #64748b; margin-top: 4px; }
          .charts { display: flex; justify-content: space-around; margin: 30px 0; padding: 20px; background: #f8fafc; border-radius: 12px; }
          .chart-container { text-align: center; }
          .chart-title { font-size: 14px; font-weight: bold; color: #475569; margin-bottom: 15px; }
          .donut-container { position: relative; width: 120px; height: 120px; margin: 0 auto; }
          .donut-center { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 24px; font-weight: bold; color: #1e293b; }
          .histogram { display: flex; align-items: flex-end; justify-content: center; gap: 8px; height: 100px; }
          .histogram-bar { width: 40px; border-radius: 4px 4px 0 0; display: flex; align-items: flex-start; justify-content: center; padding-top: 4px; color: white; font-size: 12px; font-weight: bold; }
          .histogram-labels { display: flex; justify-content: center; gap: 8px; margin-top: 8px; }
          .histogram-label { width: 40px; font-size: 10px; color: #64748b; text-align: center; }
          .legend { display: flex; justify-content: center; gap: 15px; margin-top: 10px; }
          .legend-item { display: flex; align-items: center; gap: 5px; font-size: 11px; color: #64748b; }
          .legend-dot { width: 10px; height: 10px; border-radius: 50%; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background: #f1f5f9; padding: 12px; text-align: left; font-size: 12px; text-transform: uppercase; color: #64748b; }
          td { padding: 12px; border-bottom: 1px solid #e2e8f0; }
          .status-completed { background: #dcfce7; color: #16a34a; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: bold; }
          .status-pending { background: #ffedd5; color: #ea580c; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: bold; }
          .score-high { color: #16a34a; font-weight: bold; }
          .score-medium { color: #ea580c; font-weight: bold; }
          .score-low { color: #dc2626; font-weight: bold; }
          .xp { color: #9333ea; font-weight: bold; }
          .section-title { font-size: 18px; font-weight: bold; margin: 30px 0 15px; color: #1e293b; }
          .footer { margin-top: 40px; text-align: center; color: #94a3b8; font-size: 12px; padding-top: 20px; border-top: 1px solid #e2e8f0; }
          @media print { body { padding: 20px; } .charts { break-inside: avoid; } }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>📊 Relatório de Progresso</h1>
          <h2>${missionReport.mission_title}</h2>
          <div class="date">Gerado em: ${new Date().toLocaleString('pt-BR')}</div>
        </div>
        
        <div class="summary">
          <div class="summary-card green">
            <div class="value">${missionReport.completed_count}</div>
            <div class="label">Concluídas</div>
          </div>
          <div class="summary-card orange">
            <div class="value">${missionReport.pending_count}</div>
            <div class="label">Pendentes</div>
          </div>
          <div class="summary-card purple">
            <div class="value">${missionReport.average_score}%</div>
            <div class="label">Média</div>
          </div>
        </div>
        
        <div class="charts">
          <div class="chart-container">
            <div class="chart-title">Taxa de Conclusão</div>
            <div class="donut-container">
              <svg width="120" height="120" viewBox="0 0 100 100">
                <circle cx="50" cy="50" r="40" fill="none" stroke="#fed7aa" stroke-width="12" />
                <circle cx="50" cy="50" r="40" fill="none" stroke="#16a34a" stroke-width="12" 
                  stroke-linecap="round" transform="rotate(-90 50 50)"
                  stroke-dasharray="${strokeDasharray} 251.2" />
              </svg>
              <div class="donut-center">${completionPercent}%</div>
            </div>
            <div class="legend">
              <div class="legend-item"><div class="legend-dot" style="background:#16a34a"></div>Concluídas</div>
              <div class="legend-item"><div class="legend-dot" style="background:#fed7aa"></div>Pendentes</div>
            </div>
          </div>
          
          <div class="chart-container">
            <div class="chart-title">Distribuição de Notas</div>
            ${completedStudents.length > 0 ? `
              <div class="histogram">
                ${ranges.map(r => `
                  <div class="histogram-bar" style="background:${r.color};height:${Math.max((r.count / maxCount) * 80, r.count > 0 ? 20 : 0)}px">
                    ${r.count > 0 ? r.count : ''}
                  </div>
                `).join('')}
              </div>
              <div class="histogram-labels">
                ${ranges.map(r => `<div class="histogram-label">${r.label}</div>`).join('')}
              </div>
            ` : '<div style="color:#94a3b8;padding:40px">Nenhuma nota ainda</div>'}
          </div>
        </div>
        
        ${completedStudents.length > 0 ? `
          <div class="section-title">✅ Alunos que Concluíram (${completedStudents.length})</div>
          <table>
            <thead>
              <tr>
                <th>Aluno</th>
                <th>Nota</th>
                <th>AURA</th>
              </tr>
            </thead>
            <tbody>
              ${completedStudents.map(s => `
                <tr>
                  <td>${s.name}</td>
                  <td class="${s.percentage >= 70 ? 'score-high' : s.percentage >= 50 ? 'score-medium' : 'score-low'}">
                    ${s.correct_count}/${s.total_questions} (${s.percentage}%)
                  </td>
                  <td class="xp">+${s.xp_earned} XP</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        ` : ''}
        
        ${pendingStudents.length > 0 ? `
          <div class="section-title">⏳ Alunos Pendentes (${pendingStudents.length})</div>
          <table>
            <thead>
              <tr>
                <th>Aluno</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${pendingStudents.map(s => `
                <tr>
                  <td>${s.name}</td>
                  <td><span class="status-pending">Pendente</span></td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        ` : ''}
        
        <div class="footer">
          BelottoPokeClass Center - Relatório gerado automaticamente
        </div>
      </body>
      </html>
    `;
    
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const blobUrl = URL.createObjectURL(blob);
    printWindow.location.href = blobUrl;
    printWindow.addEventListener('load', () => {
      printWindow.print();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    });
    
    toast.success('Preparando PDF para impressão...');
  };

  // Unarchive mission
  const openUnarchiveDialog = (mission) => {
    setUnarchivingMission(mission);
    setUnarchiveDialogOpen(true);
  };

  const handleUnarchiveMission = async (resetProgress) => {
    if (!unarchivingMission) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/missions/${unarchivingMission.mission_id}/unarchive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reset_progress: resetProgress })
      });
      
      if (!response.ok) throw new Error('Failed to unarchive');
      
      const data = await response.json();
      toast.success(data.message || 'Missão reativada!', { icon: '🔄' });
      
      setUnarchiveDialogOpen(false);
      setUnarchivingMission(null);
      loadData();
    } catch (error) {
      console.error('Failed to unarchive mission:', error);
      toast.error('Erro ao reativar missão');
    }
  };

  // Open remove student confirmation dialog
  const openRemoveStudentDialog = (student) => {
    console.log('[TeacherClassroom] Opening remove dialog for student:', student.student_id, student.user_name);
    setStudentToRemove({
      student_id: student.student_id,
      user_name: student.user_name,
      monster_name: student.monster?.name || null
    });
    setRemoveStudentDialogOpen(true);
  };

  // Confirm and execute student removal
  const handleConfirmRemoveStudent = async () => {
    if (!studentToRemove) {
      console.error('[TeacherClassroom] No student to remove');
      return;
    }

    const { student_id, user_name } = studentToRemove;
    console.log('[TeacherClassroom] Confirming removal of student:', student_id);
    
    setIsRemovingStudent(true);

    try {
      // Call API to remove student
      await studentAPI.removeStudent(student_id);
      
      // Close dialog first
      setRemoveStudentDialogOpen(false);
      
      // Start fade-out animation
      setRemovingStudentId(student_id);
      
      // Wait for fade-out animation, then remove from state
      setTimeout(() => {
        setStudents(prev => prev.filter(s => s.student_id !== student_id));
        setRemovingStudentId(null);
        
        // Show success toast
        toast.success(
          `${user_name} foi removido da sala com sucesso!`,
          { 
            duration: 4000,
            icon: '✅'
          }
        );
      }, 400); // Match CSS transition duration
      
    } catch (error) {
      console.error('[TeacherClassroom] Failed to remove student:', error);
      toast.error('Erro ao remover aluno. Tente novamente.');
    } finally {
      setIsRemovingStudent(false);
      setStudentToRemove(null);
    }
  };

  // Cancel student removal
  const handleCancelRemoveStudent = () => {
    setRemoveStudentDialogOpen(false);
    setStudentToRemove(null);
  };

  // Clone Mission functions
  const openCloneMissionDialog = async (mission) => {
    setMissionToClone(mission);
    setSelectedClassroomsForClone([]);
    
    // Load teacher's classrooms
    try {
      const response = await classroomAPI.getAll();
      // Filter out the current classroom
      const otherClassrooms = response.data.filter(c => c.classroom_id !== classroomId);
      setTeacherClassrooms(otherClassrooms);
    } catch (error) {
      console.error('Failed to load classrooms:', error);
      toast.error('Erro ao carregar turmas');
      return;
    }
    
    setCloneMissionDialogOpen(true);
  };

  const toggleClassroomSelection = (classroomId) => {
    setSelectedClassroomsForClone(prev => {
      if (prev.includes(classroomId)) {
        return prev.filter(id => id !== classroomId);
      } else {
        return [...prev, classroomId];
      }
    });
  };

  // ============ TRANSFER STUDENT ============
  const handleConfirmTransfer = async () => {
    setTransferError('');
    if (!studentToTransfer || !transferTargetClassroomId) {
      toast.error('Selecione uma turma de destino');
      setTransferError('Selecione uma turma de destino antes de confirmar.');
      return;
    }
    setIsTransferringStudent(true);
    try {
      const url = `${BACKEND_URL}/api/students/${studentToTransfer.student_id}/transfer`;
      console.log('[TRANSFER] POST', url, { target_classroom_id: transferTargetClassroomId });
      const resp = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target_classroom_id: transferTargetClassroomId })
      });
      const responseBody = await resp.json().catch(() => ({}));
      console.log('[TRANSFER] response status:', resp.status, 'body:', responseBody);
      if (!resp.ok) {
        const detail = responseBody.detail || `HTTP ${resp.status}`;
        throw new Error(detail);
      }
      toast.success(responseBody.message || 'Aluno transferido!', { icon: '🚀' });
      setTransferDialogOpen(false);
      setStudentToTransfer(null);
      setTransferTargetClassroomId('');
      setTransferError('');
      loadData();
    } catch (error) {
      console.error('[TRANSFER] failed:', error);
      const msg = error?.message || 'Erro ao transferir aluno';
      toast.error(msg);
      setTransferError(msg);
    } finally {
      setIsTransferringStudent(false);
    }
  };

  const openTransferDialog = async (student) => {
    setStudentToTransfer(student);
    setTransferTargetClassroomId('');
    setTransferError('');
    try {
      const response = await classroomAPI.getAll();
      const others = (response.data || []).filter(c => c.classroom_id !== student.classroom_id && c.is_active !== false);
      setTeacherClassrooms(others);
      if (others.length === 0) {
        setTransferError('Você não tem outra turma para transferir este aluno. Crie uma nova turma antes.');
      }
    } catch (error) {
      console.error('Failed to load classrooms:', error);
      toast.error('Erro ao carregar turmas');
      setTransferError('Erro ao carregar lista de turmas.');
      return;
    }
    setTransferDialogOpen(true);
  };

  // ============ BULK TRANSFER ============
  const openBulkTransferDialog = async () => {
    if (selectedStudents.size === 0) {
      toast.error('Selecione ao menos 1 aluno');
      return;
    }
    setBulkTransferTargetId('');
    setBulkTransferResult(null);
    try {
      const response = await classroomAPI.getAll();
      const others = (response.data || []).filter(c => c.classroom_id !== classroomId && c.is_active !== false);
      setTeacherClassrooms(others);
      if (others.length === 0) {
        toast.error('Você não tem outra turma. Crie uma nova turma antes.');
        return;
      }
    } catch {
      toast.error('Erro ao carregar turmas');
      return;
    }
    setBulkTransferDialogOpen(true);
  };

  const handleConfirmBulkTransfer = async () => {
    if (!bulkTransferTargetId || selectedStudents.size === 0) {
      toast.error('Selecione uma turma de destino');
      return;
    }
    setIsBulkTransferring(true);
    setBulkTransferResult(null);
    try {
      const ids = Array.from(selectedStudents);
      const resp = await fetch(`${BACKEND_URL}/api/students/bulk-transfer`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ student_ids: ids, target_classroom_id: bulkTransferTargetId })
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data.detail || `HTTP ${resp.status}`);
      }
      setBulkTransferResult(data.results);
      const moved = data.results?.transferred?.length || 0;
      const errs = data.results?.errors?.length || 0;
      if (moved > 0 && errs === 0) {
        toast.success(data.message || `${moved} aluno(s) transferido(s)!`, { icon: '🚀' });
      } else if (moved > 0) {
        toast.success(`${moved} transferido(s) | ${errs} com erro`, { icon: '⚠️' });
      } else {
        toast.error('Nenhum aluno foi transferido. Veja os detalhes abaixo.');
      }
      clearSelection();
      loadData();
    } catch (error) {
      console.error('[BULK TRANSFER] failed:', error);
      toast.error(error.message || 'Erro ao transferir alunos');
    } finally {
      setIsBulkTransferring(false);
    }
  };

  const openRestoreDialog = async () => {
    setLoadingInactive(true);
    setRestoreDialogOpen(true);
    try {
      const [inactiveResp, classroomsResp] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/inactive-students`, { credentials: 'include' }),
        classroomAPI.getAll()
      ]);
      if (!inactiveResp.ok) throw new Error('Erro ao carregar alunos removidos');
      const data = await inactiveResp.json();
      setInactiveStudents(data.inactive_students || []);
      const allClassrooms = (classroomsResp.data || []).filter(c => c.is_active !== false);
      setTeacherClassrooms(allClassrooms);

      // Default destination: original classroom
      const defaults = {};
      (data.inactive_students || []).forEach(s => {
        defaults[s.student_id] = s.classroom_id;
      });
      setRestoreTargets(defaults);
    } catch (error) {
      toast.error(error.message || 'Erro ao carregar alunos removidos');
    } finally {
      setLoadingInactive(false);
    }
  };

  const handleConfirmRestore = async (student) => {
    const target = restoreTargets[student.student_id] || student.classroom_id;
    setRestoringStudentId(student.student_id);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/admin/restore-student/${student.student_id}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ target_classroom_id: target })
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'Erro ao restaurar');
      }
      toast.success('Aluno restaurado com sucesso!', { icon: '🔄' });
      // Remove from list locally
      setInactiveStudents(prev => prev.filter(s => s.student_id !== student.student_id));
      loadData();
    } catch (error) {
      toast.error(error.message || 'Erro ao restaurar aluno');
    } finally {
      setRestoringStudentId(null);
    }
  };


  const handleCloneMission = async () => {
    if (!missionToClone || selectedClassroomsForClone.length === 0) {
      toast.error('Selecione pelo menos uma turma');
      return;
    }

    setIsCloningMission(true);
    
    try {
      const response = await classroomAPI.cloneMission(missionToClone.mission_id, selectedClassroomsForClone);
      const { cloned_to_classrooms } = response.data;
      
      toast.success(
        `Missão replicada com sucesso para ${cloned_to_classrooms.join(', ')}!`,
        { duration: 5000, icon: '🎯' }
      );
      
      setCloneMissionDialogOpen(false);
      setMissionToClone(null);
      setSelectedClassroomsForClone([]);
    } catch (error) {
      console.error('Failed to clone mission:', error);
      toast.error('Erro ao clonar missão. Tente novamente.');
    } finally {
      setIsCloningMission(false);
    }
  };

  const handleCancelCloneMission = () => {
    setCloneMissionDialogOpen(false);
    setMissionToClone(null);
    setSelectedClassroomsForClone([]);
  };

  // ===== Lab Research Functions =====
  const loadLabMaterials = useCallback(async () => {
    try {
      const resp = await fetch(`${BACKEND_URL}/api/lab/materials/${classroomId}`, { credentials: 'include' });
      if (resp.ok) setLabMaterials(await resp.json());
    } catch (e) { console.error('Load lab materials error:', e); }
  }, [classroomId]);

  const loadLabAnalytics = useCallback(async () => {
    try {
      const resp = await fetch(`${BACKEND_URL}/api/lab/analytics/${classroomId}`, { credentials: 'include' });
      if (resp.ok) setLabAnalytics(await resp.json());
    } catch (e) { console.error('Load lab analytics error:', e); }
  }, [classroomId]);

  const handleLabUpload = async () => {
    if (!labUploadForm.title.trim()) { toast.error('Título obrigatório'); return; }
    if (labUploadForm.material_type === 'file' && !labFile) { toast.error('Selecione um arquivo'); return; }
    if (labUploadForm.material_type === 'link' && !labUploadForm.link_url.trim()) { toast.error('URL obrigatória'); return; }

    setLabUploading(true);
    try {
      const formData = new FormData();
      const params = new URLSearchParams({
        classroom_id: classroomId,
        title: labUploadForm.title.trim(),
        area: labUploadForm.area || 'Geral',
        material_type: labUploadForm.material_type
      });
      if (labUploadForm.material_type === 'link') params.set('link_url', labUploadForm.link_url.trim());
      if (labFile) formData.append('file', labFile);

      const resp = await fetch(`${BACKEND_URL}/api/lab/upload?${params.toString()}`, {
        method: 'POST', credentials: 'include', body: labUploadForm.material_type === 'file' ? formData : undefined
      });
      if (resp.ok) {
        toast.success('Material enviado com sucesso!');
        setLabUploadForm({ title: '', area: 'Geral', material_type: 'file', link_url: '' });
        setLabFile(null);
        loadLabMaterials();
        loadLabAnalytics();
      } else {
        const err = await resp.json();
        toast.error(err.detail || 'Erro no upload');
      }
    } catch (e) {
      toast.error('Erro ao enviar material');
    } finally {
      setLabUploading(false);
    }
  };

  const toggleLabVisibility = async (materialId, currentVisible) => {
    try {
      const resp = await fetch(`${BACKEND_URL}/api/lab/materials/${materialId}`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ visible: !currentVisible })
      });
      if (resp.ok) { loadLabMaterials(); toast.success(currentVisible ? 'Material ocultado' : 'Material visível'); }
    } catch (e) { toast.error('Erro ao alterar visibilidade'); }
  };

  const deleteLabMaterial = async (materialId) => {
    try {
      const resp = await fetch(`${BACKEND_URL}/api/lab/materials/${materialId}`, { method: 'DELETE', credentials: 'include' });
      if (resp.ok) { loadLabMaterials(); loadLabAnalytics(); toast.success('Material removido'); }
    } catch (e) { toast.error('Erro ao remover'); }
  };

  const rewardTopResearchers = async () => {
    setLabRewarding(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/lab/reward-top/${classroomId}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ coins: labRewardCoins })
      });
      if (resp.ok) {
        const data = await resp.json();
        toast.success(`${data.rewarded.length} pesquisadores recompensados com ${data.coins_per_student} coins cada!`);
        loadLabAnalytics();
      } else {
        const err = await resp.json();
        toast.error(err.detail || 'Erro');
      }
    } catch (e) { toast.error('Erro ao recompensar'); }
    finally { setLabRewarding(false); }
  };

  const formatLabTime = (s) => {
    if (!s || s <= 0) return '0s';
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) return `${h}h ${m}m`;
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
  };

  // ===== Essay Correction Functions =====
  const openEssayCorrection = async (missionId) => {
    setLoadingEssays(true);
    setEssayDialogOpen(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/missions/${missionId}/essay-submissions`, { credentials: 'include' });
      if (resp.ok) {
        const data = await resp.json();
        setEssaySubmissions(data.submissions);
        setEssaySummary(data.summary);
      }
    } catch (e) { console.error('Load essays error:', e); }
    finally { setLoadingEssays(false); }
  };

  const startGrading = (submission) => {
    setGradingEssay(submission);
    const edits = {};
    for (const ans of submission.answers) {
      edits[ans.question_index] = {
        score: ans.teacher_score ?? ans.ai_score,
        feedback: ans.teacher_feedback ?? ans.ai_feedback
      };
    }
    setGradeEdits(edits);
  };

  const submitGrade = async () => {
    if (!gradingEssay) return;
    setSubmittingGrade(true);
    try {
      const grades = Object.entries(gradeEdits).map(([qi, g]) => ({
        question_index: parseInt(qi), score: g.score, feedback: g.feedback, teacher_image_url: g.teacher_image_url || null
      }));
      const resp = await fetch(`${BACKEND_URL}/api/essay/${gradingEssay.submission_id}/grade`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ grades })
      });
      if (resp.ok) {
        const data = await resp.json();
        toast.success(data.message);
        setGradingEssay(null);
        // Reload submissions
        if (essaySubmissions?.[0]?.mission_id) openEssayCorrection(essaySubmissions[0].mission_id);
      } else { const err = await resp.json(); toast.error(err.detail || 'Erro'); }
    } catch (e) { toast.error('Erro ao salvar avaliação'); }
    finally { setSubmittingGrade(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary"></div>
      </div>
    );
  }

  if (!classroom) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#F8FAFC]">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-700 mb-4">Sala não encontrada</h2>
          <p className="text-slate-500 mb-6">A sala de aula solicitada não existe ou você não tem permissão para acessá-la.</p>
          <Button onClick={() => navigate('/teacher/dashboard')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar ao Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <nav className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-4 py-4">
          <Button
            data-testid="back-btn"
            onClick={() => navigate('/teacher/dashboard')}
            variant="ghost"
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </Button>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-white rounded-2xl border-2 border-slate-100 p-8 mb-8">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-outfit font-bold text-slate-900 mb-2" data-testid="classroom-name">{classroom.name}</h1>
              <span className="text-slate-600 font-nunito">{students.length} alunos</span>
            </div>
            <div className="flex items-center gap-6">
              <TeacherNotificationBell classroomId={classroomId} classroomName={classroom.name} />
              <div className="text-right">
                <p className="text-sm text-slate-600 font-nunito mb-1">Código de Acesso</p>
                <p className="text-2xl font-mono font-black text-primary" data-testid="access-code">{classroom.access_code}</p>
              </div>
            </div>
          </div>
        </div>

        <Tabs defaultValue="students" className="space-y-6">
          <TabsList className="bg-white/80 backdrop-blur-sm border border-slate-200 p-1.5 rounded-2xl shadow-sm gap-1">
            <TabsTrigger
              value="students"
              data-testid="students-tab"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-indigo-600 data-[state=active]:text-white data-[state=active]:shadow-md rounded-xl px-5 py-2.5 font-bold text-sm transition-all duration-200 flex items-center gap-2"
            >
              <Users className="w-4 h-4" />
              Alunos
            </TabsTrigger>
            <TabsTrigger
              value="groups"
              data-testid="groups-tab"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-500 data-[state=active]:to-violet-600 data-[state=active]:text-white data-[state=active]:shadow-md rounded-xl px-5 py-2.5 font-bold text-sm transition-all duration-200 flex items-center gap-2"
            >
              <Shield className="w-4 h-4" />
              Grupos
            </TabsTrigger>
            <TabsTrigger
              value="tasks"
              data-testid="tasks-tab"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-500 data-[state=active]:to-teal-600 data-[state=active]:text-white data-[state=active]:shadow-md rounded-xl px-5 py-2.5 font-bold text-sm transition-all duration-200 flex items-center gap-2"
            >
              <Target className="w-4 h-4" />
              Tarefas
            </TabsTrigger>
            <TabsTrigger
              value="missions"
              data-testid="missions-tab"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-amber-500 data-[state=active]:to-orange-600 data-[state=active]:text-white data-[state=active]:shadow-md rounded-xl px-5 py-2.5 font-bold text-sm transition-all duration-200 flex items-center gap-2"
            >
              <Sparkles className="w-4 h-4" />
              Missoes
            </TabsTrigger>
            <TabsTrigger
              value="lab"
              data-testid="lab-tab"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-teal-500 data-[state=active]:to-cyan-600 data-[state=active]:text-white data-[state=active]:shadow-md rounded-xl px-5 py-2.5 font-bold text-sm transition-all duration-200 flex items-center gap-2"
              onClick={() => { loadLabMaterials(); loadLabAnalytics(); }}
            >
              <FlaskConical className="w-4 h-4" />
              Lab
            </TabsTrigger>
          </TabsList>

          <TabsContent value="students">
            {/* Leaderboard Card */}
            {leaderboard.length > 0 && (
              <div className="mb-6 bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 rounded-2xl border-2 border-amber-200 p-6 shadow-lg" data-testid="leaderboard-card">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-xl flex items-center justify-center shadow-md">
                    <Trophy className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-outfit font-bold text-lg text-slate-800">Ranking da Turma</h3>
                    <p className="text-sm text-slate-500">Top 5 alunos por nível e AURA</p>
                  </div>
                </div>
                
                <div className="space-y-2">
                  {leaderboard.map((student, idx) => {
                    const medalColors = {
                      1: { bg: 'bg-gradient-to-r from-amber-400 to-yellow-500', text: 'text-amber-900', border: 'border-amber-300', shadow: 'shadow-amber-200' },
                      2: { bg: 'bg-gradient-to-r from-slate-300 to-slate-400', text: 'text-slate-700', border: 'border-slate-300', shadow: 'shadow-slate-200' },
                      3: { bg: 'bg-gradient-to-r from-orange-400 to-amber-600', text: 'text-orange-900', border: 'border-orange-300', shadow: 'shadow-orange-200' }
                    };
                    const medal = medalColors[student.rank];
                    const isTop3 = student.rank <= 3;
                    
                    return (
                      <div 
                        key={student.student_id}
                        className={`flex items-center gap-3 p-3 rounded-xl transition-all duration-300 hover:scale-[1.02] ${
                          isTop3 
                            ? `bg-white/80 border-2 ${medal.border} shadow-md ${medal.shadow}` 
                            : 'bg-white/50 border border-slate-200'
                        }`}
                        data-testid={`leaderboard-position-${student.rank}`}
                      >
                        {/* Position Badge */}
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm ${
                          isTop3 ? `${medal.bg} text-white shadow-md` : 'bg-slate-200 text-slate-600'
                        }`}>
                          {student.rank === 1 ? <Crown className="w-4 h-4" /> : student.rank}
                        </div>
                        
                        {/* Monster Avatar */}
                        <div className="w-10 h-10 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0">
                          {student.monster ? (
                            <PokemonImage
                              src={getMonsterGifByMonster(student.monster)}
                              fallbackSrc={getMonsterFallbackGif(student.monster)}
                              alt={getMonsterDisplayName(student.monster)}
                              className="w-full h-full object-contain"
                              fallbackSize={40}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              {eggGif ? (
                                <img src={eggGif} alt="Ovo" className="w-8 h-8 object-contain" />
                              ) : (
                                <span className="text-lg">🥚</span>
                              )}
                            </div>
                          )}
                        </div>
                        
                        {/* Student Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`font-semibold truncate ${isTop3 ? medal.text : 'text-slate-700'}`}>
                              {student.user_name}
                            </span>
                            {student.win_streak >= 2 && (
                              <span className="text-xs">🔥x{student.win_streak}</span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500">
                            Nível {student.level || 1} • {student.monster?.name || 'Ovo'}
                          </div>
                        </div>
                        
                        {/* XP Badge */}
                        <div className={`px-3 py-1.5 rounded-full font-bold text-sm ${
                          isTop3 
                            ? 'bg-purple-100 text-purple-700' 
                            : 'bg-slate-100 text-slate-600'
                        }`}>
                          <Sparkles className="w-3 h-3 inline mr-1" />
                          {student.xp || 0} AURA
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                {students.length > 5 && (
                  <div className="text-center mt-3 text-sm text-slate-500">
                    +{students.length - 5} outros alunos na turma
                  </div>
                )}
              </div>
            )}
            
            {/* Header: Select All + Actions */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <button
                  data-testid="select-all-students-checkbox"
                  onClick={toggleSelectAll}
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all cursor-pointer ${
                    selectedStudents.size === students.filter(s => s.egg_hatched).length && students.filter(s => s.egg_hatched).length > 0
                      ? 'bg-indigo-600 border-indigo-600'
                      : selectedStudents.size > 0
                        ? 'bg-indigo-300 border-indigo-400'
                        : 'border-slate-300 hover:border-indigo-400'
                  }`}
                >
                  {selectedStudents.size > 0 && (
                    <Check className="w-3 h-3 text-white" />
                  )}
                </button>
                <span className="text-sm text-slate-500 font-nunito">
                  {selectedStudents.size > 0
                    ? `${selectedStudents.size} selecionado${selectedStudents.size !== 1 ? 's' : ''}`
                    : 'Selecionar alunos'}
                </span>
              </div>
              <Button
                data-testid="bulk-xp-btn"
                onClick={() => setBulkXPDialogOpen(true)}
                size="sm"
                className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white font-bold"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Atribuir AURA
              </Button>
            </div>
            
            {/* Student Cards Grid — redesigned for readability & breathing room */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
              {sortedStudents.map((student) => {
                const isSelected = selectedStudents.has(student.student_id);
                const hasAnySelected = selectedStudents.size > 0;
                return (
                <div 
                  key={`${student.student_id}-${student.xp}-${student.hp}-${student.level}-${student.energy}-${student.monster?.name || ''}`} 
                  data-testid="student-card" 
                  className={`group/card relative bg-white rounded-2xl border-2 p-5 transition-all duration-200 cursor-pointer ${
                    removingStudentId === student.student_id 
                      ? 'opacity-0 scale-95' 
                      : 'opacity-100 scale-100'
                  } ${
                    isSelected
                      ? 'border-indigo-400 bg-indigo-50/40 shadow-lg ring-2 ring-indigo-200'
                      : 'border-slate-100 hover:border-slate-200 hover:shadow-lg'
                  }`}
                  onClick={() => student.egg_hatched && toggleStudentSelection(student.student_id)}
                >
                  {/* Checkbox (top-left) */}
                  {student.egg_hatched && (
                    <div
                      data-testid="student-checkbox"
                      className={`absolute top-3 left-3 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all z-10 ${
                        isSelected
                          ? 'bg-indigo-600 border-indigo-600'
                          : hasAnySelected
                            ? 'border-slate-300 bg-white'
                            : 'border-slate-300 bg-white opacity-0 group-hover/card:opacity-100'
                      }`}
                      onClick={(e) => { e.stopPropagation(); toggleStudentSelection(student.student_id); }}
                    >
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </div>
                  )}

                  {/* Menu (top-right) - MoreVertical */}
                  {student.egg_hatched && (
                    <div className="absolute top-3 right-3 opacity-0 group-hover/card:opacity-100 transition-opacity z-10" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            data-testid="student-menu-btn"
                            className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-slate-100 transition-colors"
                          >
                            <MoreVertical className="w-4 h-4 text-slate-500" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-52">
                          <DropdownMenuLabel className="text-xs text-slate-400 uppercase tracking-wider">AURA</DropdownMenuLabel>
                          <div className="flex gap-1 px-2 py-1">
                            {[5, 10, 50].map((xp) => (
                              <button
                                key={`add-${xp}`}
                                data-testid={`increase-xp-${xp}-btn`}
                                onClick={() => handleAdjustXP(student.student_id, xp)}
                                className="flex-1 text-xs px-1.5 py-1 rounded bg-green-50 text-green-700 hover:bg-green-100 font-semibold transition-colors"
                              >
                                +{xp}
                              </button>
                            ))}
                          </div>
                          <div className="flex gap-1 px-2 py-1">
                            {[5, 10, 50].map((xp) => (
                              <button
                                key={`sub-${xp}`}
                                data-testid={`decrease-xp-${xp}-btn`}
                                onClick={() => handleAdjustXP(student.student_id, -xp)}
                                className="flex-1 text-xs px-1.5 py-1 rounded bg-orange-50 text-orange-700 hover:bg-orange-100 font-semibold transition-colors"
                              >
                                -{xp}
                              </button>
                            ))}
                          </div>
                          <DropdownMenuSeparator />
                          <DropdownMenuLabel className="text-xs text-slate-400 uppercase tracking-wider">HP</DropdownMenuLabel>
                          <div className="flex gap-1 px-2 py-1">
                            <button onClick={() => handleHealStudent(student.student_id, 5)} className="flex-1 text-xs px-1.5 py-1 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-semibold">+5</button>
                            <button onClick={() => handleHealStudent(student.student_id, 10)} className="flex-1 text-xs px-1.5 py-1 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-semibold">+10</button>
                            <button onClick={() => handleApplyPenalty(student.student_id, 10)} className="flex-1 text-xs px-1.5 py-1 rounded bg-red-50 text-red-700 hover:bg-red-100 font-semibold">-10</button>
                          </div>
                          <DropdownMenuSeparator />
                          <DropdownMenuLabel className="text-xs text-slate-400 uppercase tracking-wider">Energia</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => handleRechargeEnergy(student.student_id, 20)} className="cursor-pointer">
                            <Zap className="w-4 h-4 text-blue-500" />
                            <span>+20 Energia</span>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuLabel className="text-xs text-slate-400 uppercase tracking-wider">Comportamento</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => openBehaviorDialog(student, 'positive')} className="cursor-pointer" disabled={Object.keys(behaviors.positive).length === 0}>
                            <ThumbsUp className="w-4 h-4 text-green-500" />
                            <span>Comportamento Positivo</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openBehaviorDialog(student, 'negative')} className="cursor-pointer" disabled={Object.keys(behaviors.negative).length === 0}>
                            <ThumbsDown className="w-4 h-4 text-red-500" />
                            <span>Comportamento Negativo</span>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuLabel className="text-xs text-slate-400 uppercase tracking-wider">Gerenciar</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => handleEvolveMonster(student.student_id)} disabled={student.level < 16} className="cursor-pointer">
                            <Sparkles className="w-4 h-4 text-purple-500" />
                            <span>Evoluir Monstro</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => startEditingMonsterName(student.student_id, getMonsterDisplayName(student.monster))} className="cursor-pointer">
                            <Pencil className="w-4 h-4 text-slate-500" />
                            <span>Renomear Monstro</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openChangePokemonDialog(student)} className="cursor-pointer">
                            <RefreshCw className="w-4 h-4 text-blue-500" />
                            <span>Trocar Pokemon</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openTransferDialog(student)} className="cursor-pointer" data-testid={`transfer-student-${student.student_id}`}>
                            <Send className="w-4 h-4 text-cyan-500" />
                            <span>Transferir para outra turma</span>
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => openRemoveStudentDialog(student)} className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50">
                            <UserMinus className="w-4 h-4" />
                            <span>Remover da Sala</span>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )}

                  {/* Card Content — bigger sprite, larger name, more air */}
                  <div className="flex items-center gap-4 mb-4 mt-1">
                    {/* Avatar — enlarged 12→20 (67% bigger) */}
                    <div className="w-20 h-20 flex-shrink-0 rounded-xl bg-gradient-to-br from-slate-50 to-slate-100 border-2 border-slate-100 flex items-center justify-center p-1">
                      {student.monster ? (
                        <PokemonImage
                          src={getMonsterGifByMonster(student.monster)}
                          fallbackSrc={getMonsterFallbackGif(student.monster)}
                          alt={getMonsterDisplayName(student.monster)}
                          className="w-full h-full object-contain drop-shadow-sm"
                          fallbackSize={72}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center overflow-hidden">
                          {eggGif ? (
                            <img src={eggGif} alt="Ovo" className="w-16 h-16 object-contain" />
                          ) : (
                            <span className="text-4xl">🥚</span>
                          )}
                        </div>
                      )}
                    </div>
                    {/* Name + Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {editingMonsterName === student.student_id ? (
                          <div className="flex items-center gap-1 flex-1" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="text"
                              data-testid="monster-name-input"
                              value={tempMonsterName}
                              onChange={(e) => setTempMonsterName(e.target.value)}
                              onBlur={() => handleRenameMonster(student.student_id)}
                              onKeyDown={(e) => handleMonsterNameKeyDown(e, student.student_id)}
                              autoFocus
                              className="flex-1 px-2 py-1 text-base font-bold text-slate-900 border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                              placeholder="Nome"
                            />
                            <button onClick={() => handleRenameMonster(student.student_id)} className="p-0.5 text-green-600 hover:bg-green-100 rounded">
                              <Check className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <>
                            <h3 className="font-outfit font-black text-base leading-tight text-slate-900 truncate" data-testid="student-name-display">
                              {student.user_name}
                            </h3>
                            {student.win_streak >= 2 && (
                              <span className="text-sm flex-shrink-0" title={`Combo x${student.win_streak}`}>🔥x{student.win_streak}</span>
                            )}
                          </>
                        )}
                      </div>
                      {student.monster ? (
                        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                          <span data-testid="monster-name-display" className="text-sm text-slate-600 font-medium truncate max-w-[110px]">{getMonsterDisplayName(student.monster)}</span>
                          <span className={`px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wider border ${getClassBadge(student.monster.class_type)}`}>
                            {getClassName(student.monster.class_type)}
                          </span>
                        </div>
                      ) : (
                        <p className="text-xs text-slate-400 truncate mt-1">{student.user_email}</p>
                      )}
                    </div>
                  </div>

                  {!student.egg_hatched ? (
                    /* Egg Hatch UI - roomy */
                    <div className="space-y-2.5" onClick={(e) => e.stopPropagation()}>
                      <select
                        value={student.assigned_class || ''}
                        onChange={(e) => handleAssignClass(student.student_id, e.target.value)}
                        data-testid="assign-class-select"
                        className="w-full h-10 px-3 border-2 border-slate-200 rounded-xl bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="" disabled>Escolher classe...</option>
                        <option value="tank">Tank</option>
                        <option value="warrior">Atacante</option>
                        <option value="support">Suporte</option>
                      </select>
                      <Button
                        data-testid="hatch-egg-btn"
                        onClick={() => handleHatchEgg(student.student_id)}
                        disabled={!student.assigned_class}
                        size="sm"
                        className="w-full bg-secondary hover:bg-secondary/90 text-white h-10 rounded-xl font-bold text-sm"
                      >
                        <Sparkles className="w-4 h-4 mr-1.5" />
                        Chocar Ovo
                      </Button>
                      <button
                        data-testid="remove-student-btn-unhatched"
                        onClick={() => openRemoveStudentDialog(student)}
                        className="w-full text-xs text-red-500 hover:text-red-700 hover:underline py-1"
                      >
                        Remover
                      </button>
                    </div>
                  ) : (
                    /* Stat bars — thicker, more vibrant, better spacing */
                    <div className="space-y-3">
                      {/* Level / XP — level as prominent pill on the left */}
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <span
                            data-testid="student-level-badge"
                            className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg bg-gradient-to-br from-amber-100 to-orange-100 border border-amber-200 text-amber-800 font-outfit font-black text-sm"
                          >
                            <span className="text-[10px] uppercase tracking-widest text-amber-600/80">Nv</span>
                            {student.level}
                          </span>
                          <span className="text-xs text-slate-500 font-medium tabular-nums">{student.xp}/{calculateXPNeeded(student.level)} AURA</span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden shadow-inner">
                          <div
                            data-testid="xp-bar"
                            className="bg-gradient-to-r from-amber-400 via-orange-500 to-rose-500 h-2.5 rounded-full transition-all duration-500 shadow-sm aura-shimmer"
                            style={{ width: `${Math.min(100, (student.xp / calculateXPNeeded(student.level)) * 100)}%` }}
                          />
                        </div>
                      </div>
                      {/* HP — brighter red gradient, thicker bar */}
                      <div className="flex items-center gap-2">
                        <Heart className="w-4 h-4 text-rose-500 flex-shrink-0 fill-rose-500/30" />
                        <div className="flex-1 bg-slate-100 rounded-full h-2.5 overflow-hidden shadow-inner">
                          <div
                            data-testid="hp-bar"
                            className="bg-gradient-to-r from-rose-500 via-red-500 to-pink-500 h-2.5 rounded-full transition-all duration-500 shadow-sm"
                            style={{ width: `${Math.min(100, (student.hp / student.max_hp) * 100)}%` }}
                          />
                        </div>
                        <span className="text-[11px] text-slate-600 font-semibold w-14 text-right tabular-nums">{student.hp}/{student.max_hp}</span>
                      </div>
                      {/* Energy — vibrant sky-to-cyan gradient */}
                      <div className="flex items-center gap-2">
                        <Zap className="w-4 h-4 text-sky-500 flex-shrink-0 fill-sky-500/30" />
                        <div className="flex-1 bg-slate-100 rounded-full h-2.5 overflow-hidden shadow-inner">
                          <div
                            data-testid="energy-bar"
                            className="bg-gradient-to-r from-sky-400 via-blue-500 to-cyan-400 h-2.5 rounded-full transition-all duration-500 shadow-sm"
                            style={{ width: `${Math.min(100, (student.energy / student.max_energy) * 100)}%` }}
                          />
                        </div>
                        <span className="text-[11px] text-slate-600 font-semibold w-14 text-right tabular-nums">{student.energy}/{student.max_energy}</span>
                      </div>
                      {/* Coins badge — unchanged info, refreshed styling */}
                      {student.coins !== undefined && student.coins > 0 && (
                        <div className="flex items-center gap-1.5 pt-1">
                          <Coins className="w-4 h-4 text-amber-500" />
                          <span className="text-sm font-black text-amber-600 tabular-nums">{student.coins}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                );
              })}
            </div>

            {/* Floating Action Bar */}
            {selectedStudents.size > 0 && (
              <div
                data-testid="floating-action-bar"
                className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-slate-900/95 backdrop-blur-lg text-white rounded-2xl shadow-2xl px-6 py-3 flex items-center gap-4 border border-slate-700 animate-in slide-in-from-bottom-4 duration-300"
              >
                <div className="flex items-center gap-2 pr-4 border-r border-slate-600">
                  <CheckSquare className="w-4 h-4 text-indigo-400" />
                  <span className="font-bold text-sm">{selectedStudents.size}</span>
                  <span className="text-xs text-slate-300">selecionado{selectedStudents.size !== 1 ? 's' : ''}</span>
                </div>
                <button
                  data-testid="floating-bulk-xp-btn"
                  onClick={(e) => { e.stopPropagation(); setBulkXPDialogOpen(true); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-600 hover:bg-purple-500 transition-colors text-sm font-semibold"
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  AURA
                </button>
                <button
                  data-testid="floating-bulk-hp-btn"
                  onClick={(e) => { e.stopPropagation(); handleBulkHP(10); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 transition-colors text-sm font-semibold"
                >
                  <Heart className="w-3.5 h-3.5" />
                  +10 HP
                </button>
                <button
                  data-testid="floating-bulk-energy-btn"
                  onClick={(e) => { e.stopPropagation(); handleBulkEnergy(20); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 transition-colors text-sm font-semibold"
                >
                  <Zap className="w-3.5 h-3.5" />
                  +20 Energia
                </button>
                <button
                  data-testid="floating-bulk-penalty-btn"
                  onClick={(e) => { e.stopPropagation(); handleBulkHP(-10); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-600/80 hover:bg-red-500 transition-colors text-sm font-semibold"
                >
                  <Minus className="w-3.5 h-3.5" />
                  -10 HP
                </button>
                <button
                  data-testid="floating-bulk-transfer-btn"
                  onClick={(e) => { e.stopPropagation(); openBulkTransferDialog(); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-500 transition-colors text-sm font-semibold"
                >
                  <Send className="w-3.5 h-3.5" />
                  Transferir
                </button>
                <button
                  data-testid="floating-clear-selection-btn"
                  onClick={(e) => { e.stopPropagation(); clearSelection(); }}
                  className="ml-2 p-1.5 rounded-lg hover:bg-slate-700 transition-colors"
                  title="Limpar seleção"
                >
                  <X className="w-4 h-4 text-slate-400" />
                </button>
              </div>
            )}
          </TabsContent>

          {/* GROUPS TAB */}
          <TabsContent value="groups">
            <div className="space-y-6">
              {/* Create Group Button */}
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-outfit font-bold text-slate-700">Gerenciar Grupos</h3>
                <Button
                  data-testid="create-group-btn"
                  onClick={() => setCreateGroupDialogOpen(true)}
                  className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Criar Novo Grupo
                </Button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* Unassigned Students Column */}
                <div className="lg:col-span-1">
                  <div className="bg-slate-100 rounded-2xl p-4 min-h-[400px]">
                    <h4 className="font-outfit font-bold text-slate-700 mb-3 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Sem Grupo ({students.filter(s => !s.group_id).length})
                    </h4>
                    <div className="space-y-2 max-h-[500px] overflow-y-auto">
                      {students.filter(s => !s.group_id).map(student => (
                        <div
                          key={student.student_id}
                          draggable
                          onDragStart={(e) => handleDragStart(e, student)}
                          className="flex items-center gap-2 p-2 bg-white rounded-lg cursor-grab hover:shadow-md transition-all border-2 border-transparent hover:border-indigo-200"
                          data-testid={`unassigned-student-${student.student_id}`}
                        >
                          <div className="w-8 h-8 rounded-full overflow-hidden bg-slate-200 flex-shrink-0">
                            {student.monster ? (
                              <img 
                                src={getMonsterGifByMonster(student.monster)}
                                alt=""
                                loading="lazy"
                                className="w-full h-full object-contain"
                                onError={(e) => { if (!e.currentTarget.dataset.fb) { e.currentTarget.dataset.fb = '1'; e.currentTarget.src = getMonsterFallbackGif(student.monster); } }}
                              />
                            ) : (
                              <span className="w-full h-full flex items-center justify-center text-sm">🥚</span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-slate-700 truncate">{student.user_name}</p>
                            <p className="text-xs text-slate-500">Nv. {student.level || 1}</p>
                          </div>
                        </div>
                      ))}
                      {students.filter(s => !s.group_id).length === 0 && (
                        <p className="text-sm text-slate-500 text-center py-4">
                          Todos os alunos estão em grupos
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Groups Grid */}
                <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {groups.map(group => (
                    <div
                      key={group.group_id}
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, group.group_id)}
                      className="bg-white rounded-2xl border-2 shadow-sm overflow-hidden transition-all hover:shadow-md"
                      style={{ borderColor: group.color }}
                      data-testid={`group-card-${group.group_id}`}
                    >
                      {/* Group Header */}
                      <div 
                        className="px-4 py-3 flex items-center justify-between"
                        style={{ backgroundColor: group.color + '20' }}
                      >
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-4 h-4 rounded-full"
                            style={{ backgroundColor: group.color }}
                          />
                          <h4 className="font-outfit font-bold text-slate-800">{group.name}</h4>
                          <span className="text-xs bg-white/60 px-2 py-0.5 rounded-full text-slate-600">
                            {group.members?.length || 0}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => {
                              setSelectedGroupForXP(group);
                              setGroupXPDialogOpen(true);
                            }}
                            title="Atribuir AURA"
                          >
                            <Sparkles className="w-4 h-4 text-purple-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => {
                              setEditingGroup(group);
                              setNewGroupName(group.name);
                              setNewGroupColor(group.color);
                              setCreateGroupDialogOpen(true);
                            }}
                            title="Editar"
                          >
                            <Edit className="w-4 h-4 text-slate-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => handleDeleteGroup(group.group_id)}
                            title="Excluir"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>

                      {/* Group Members */}
                      <div className="p-3 min-h-[150px] space-y-2">
                        {(group.members_data || []).map(member => (
                          <div
                            key={member.student_id}
                            className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg group"
                          >
                            <div className="w-8 h-8 rounded-full overflow-hidden bg-slate-200 flex-shrink-0">
                              {member.monster ? (
                                <img 
                                  src={getMonsterGifByMonster(member.monster)}
                                  alt=""
                                  loading="lazy"
                                  className="w-full h-full object-contain"
                                  onError={(e) => { if (!e.currentTarget.dataset.fb) { e.currentTarget.dataset.fb = '1'; e.currentTarget.src = getMonsterFallbackGif(member.monster); } }}
                                />
                              ) : (
                                <span className="w-full h-full flex items-center justify-center text-sm">🥚</span>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-slate-700 truncate">{member.user_name}</p>
                              <p className="text-xs text-slate-500">Nv. {member.level} • {member.xp} AURA</p>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => handleRemoveStudentFromGroup(group.group_id, member.student_id)}
                              title="Remover do grupo"
                            >
                              <X className="w-3 h-3 text-red-500" />
                            </Button>
                          </div>
                        ))}
                        {(!group.members_data || group.members_data.length === 0) && (
                          <div className="h-full flex items-center justify-center py-8">
                            <p className="text-sm text-slate-400 text-center">
                              Arraste alunos aqui
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}

                  {groups.length === 0 && (
                    <div className="col-span-full bg-slate-50 rounded-2xl p-8 text-center">
                      <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">Nenhum grupo criado ainda</p>
                      <p className="text-sm text-slate-400 mt-1">Clique em "Criar Novo Grupo" para começar</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="tasks">
            <div className="mb-6">
              <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    data-testid="create-task-btn"
                    className="bg-primary hover:bg-primary/90 text-white h-11 px-6 rounded-xl font-bold shadow-lg flex items-center gap-2"
                  >
                    <Plus className="w-5 h-5" />
                    Nova Tarefa
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle className="font-outfit">Criar Nova Tarefa</DialogTitle>
                    <DialogDescription>
                      Preencha os campos abaixo para criar uma nova tarefa para os alunos.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateTask} className="space-y-4">
                    <div>
                      <Label htmlFor="task-title" className="font-nunito">Título</Label>
                      <Input
                        id="task-title"
                        data-testid="task-title-input"
                        value={taskForm.title}
                        onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                        placeholder="Ex: Resolver exercícios de álgebra"
                        required
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="task-description" className="font-nunito">Descrição</Label>
                      <Textarea
                        id="task-description"
                        data-testid="task-description-input"
                        value={taskForm.description}
                        onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                        placeholder="Descreva a tarefa..."
                        required
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="task-xp" className="font-nunito">AURA de Recompensa</Label>
                      <Input
                        id="task-xp"
                        data-testid="task-xp-input"
                        type="number"
                        value={taskForm.xp_reward}
                        onChange={(e) => setTaskForm({ ...taskForm, xp_reward: parseInt(e.target.value) })}
                        min="10"
                        required
                        className="mt-2"
                      />
                    </div>
                    <Button
                      data-testid="submit-task-btn"
                      type="submit"
                      className="w-full bg-primary hover:bg-primary/90 text-white h-11 rounded-xl font-bold"
                    >
                      Criar Tarefa
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-4">
              {tasks.map((task) => (
                <div key={task.task_id} data-testid="task-card" className="bg-white rounded-2xl border-2 border-slate-100 p-6 shadow-sm">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-outfit font-bold text-slate-900 mb-2">{task.title}</h3>
                      <p className="text-slate-600 font-nunito mb-4">{task.description}</p>
                    </div>
                    <div className="bg-secondary/10 text-secondary px-4 py-2 rounded-xl font-bold">
                      +{task.xp_reward} AURA
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* MISSIONS TAB */}
          <TabsContent value="missions">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-outfit font-bold text-slate-900 flex items-center gap-2">
                <Target className="w-6 h-6 text-purple-600" />
                Missões de Múltipla Escolha
              </h2>
              <div className="flex gap-2">
                <Button
                  onClick={() => navigate(`/teacher/classroom/${classroomId}/journeys`)}
                  className="bg-gradient-to-r from-violet-500 to-indigo-600 hover:from-violet-600 hover:to-indigo-700 text-white"
                  data-testid="open-journeys-btn"
                >
                  <BookOpen className="w-4 h-4 mr-2" />
                  Centro das Jornadas
                </Button>
                <Button
                  onClick={() => navigate(`/teacher/classroom/${classroomId}/duels`)}
                  className="bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white"
                  data-testid="open-duels-btn"
                >
                  <Swords className="w-4 h-4 mr-2" />
                  Arenas de Duelo
                </Button>
              <Dialog open={missionDialogOpen} onOpenChange={setMissionDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    data-testid="create-mission-btn"
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                    onClick={() => resetMissionForm()}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Criar Missão
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="font-outfit flex items-center gap-2">
                      <Target className="w-5 h-5 text-purple-600" />
                      Nova Missão
                    </DialogTitle>
                    <DialogDescription>
                      Crie uma missão de múltipla escolha com até 5 perguntas.
                    </DialogDescription>
                  </DialogHeader>
                  
                  {/* AI Generation Button */}
                  <div className="mb-4">
                    <Button
                      type="button"
                      onClick={() => {
                        setMissionDialogOpen(false);
                        setAiGeneratorOpen(true);
                      }}
                      className="w-full py-4 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                    >
                      <Sparkles className="w-5 h-5 mr-2" />
                      Gerar Missão com IA
                      <Brain className="w-4 h-4 ml-2 opacity-70" />
                    </Button>
                    <p className="text-xs text-center text-slate-500 mt-2">
                      Deixe a IA criar questões contextualizadas automaticamente
                    </p>
                  </div>
                  
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-slate-200" />
                    </div>
                    <div className="relative flex justify-center">
                      <span className="px-3 bg-white text-sm text-slate-500">ou crie manualmente</span>
                    </div>
                  </div>
                  
                  <form onSubmit={handleCreateMission} className="space-y-6 mt-4">
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="mission-title" className="font-nunito">Título da Missão</Label>
                        <Input
                          id="mission-title"
                          data-testid="mission-title-input"
                          value={missionForm.title}
                          onChange={(e) => setMissionForm(prev => ({ ...prev, title: e.target.value }))}
                          placeholder="Ex: Quiz de Matemática"
                          required
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="mission-description" className="font-nunito">Descrição (opcional)</Label>
                        <Textarea
                          id="mission-description"
                          data-testid="mission-description-input"
                          value={missionForm.description}
                          onChange={(e) => setMissionForm(prev => ({ ...prev, description: e.target.value }))}
                          placeholder="Descrição da missão..."
                          className="mt-2"
                          rows={2}
                        />
                      </div>
                      <div>
                        <Label htmlFor="mission-expires" className="font-nunito flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          Prazo de Encerramento <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="mission-expires"
                          data-testid="mission-expires-input"
                          type="datetime-local"
                          value={isoToLocalInputValue(missionForm.expires_at)}
                          onChange={(e) => setMissionForm(prev => ({ 
                            ...prev, 
                            expires_at: e.target.value ? new Date(e.target.value).toISOString() : ''
                          }))}
                          className="mt-2"
                          min={nowLocalInputValue()}
                          required
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          Obrigatório — define quando o gabarito será liberado automaticamente para a turma
                        </p>
                      </div>
                      <div>
                        <Label htmlFor="mission-coins" className="font-nunito flex items-center gap-2">
                          <Coins className="w-4 h-4 text-amber-500" />
                          Coins por Questao Correta
                        </Label>
                        <Input
                          id="mission-coins"
                          data-testid="mission-coins-input"
                          type="number"
                          min="0"
                          value={missionForm.coins_per_question}
                          onChange={(e) => setMissionForm(prev => ({ ...prev, coins_per_question: parseInt(e.target.value) || 0 }))}
                          className="mt-2"
                          placeholder="Ex: 5"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          {missionForm.questions.length} questoes x {missionForm.coins_per_question} = ate {missionForm.questions.length * missionForm.coins_per_question} coins
                          {missionForm.perfect_bonus_pct > 0 && ` (+${missionForm.perfect_bonus_pct}% bonus gabarito)`}
                        </p>
                      </div>
                    </div>

                    {/* Lab References */}
                    {labMaterials.length > 0 && (
                      <div className="p-4 bg-teal-50 rounded-xl border border-teal-100">
                        <Label className="text-sm font-bold text-teal-700 flex items-center gap-1.5 mb-2">
                          <FlaskConical className="w-4 h-4" />
                          Referências do Laboratório (opcional)
                        </Label>
                        <div className="space-y-1.5">
                          {labMaterials.map(mat => {
                            const isSelected = (missionForm.lab_references || []).includes(mat.material_id);
                            return (
                              <button
                                key={mat.material_id}
                                type="button"
                                data-testid={`lab-ref-${mat.material_id}`}
                                onClick={() => {
                                  setMissionForm(prev => {
                                    const refs = prev.lab_references || [];
                                    return { ...prev, lab_references: isSelected ? refs.filter(r => r !== mat.material_id) : [...refs, mat.material_id] };
                                  });
                                }}
                                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-xs transition-all ${
                                  isSelected ? 'bg-teal-200 border border-teal-400 text-teal-800 font-bold' : 'bg-white border border-slate-200 text-slate-600 hover:border-teal-300'
                                }`}
                              >
                                {isSelected ? <Check className="w-3.5 h-3.5 flex-shrink-0" /> : <Plus className="w-3.5 h-3.5 flex-shrink-0 text-slate-400" />}
                                <span className="truncate">{mat.title}</span>
                                <span className="text-[10px] text-slate-400 ml-auto">{mat.area || 'Geral'}</span>
                              </button>
                            );
                          })}
                        </div>
                        {(missionForm.lab_references || []).length > 0 && (
                          <p className="text-[10px] text-teal-600 mt-1.5">{(missionForm.lab_references || []).length} referência(s) selecionada(s) - serão exibidas ao aluno na missão</p>
                        )}
                      </div>
                    )}

                    {/* Questions */}
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="font-outfit font-bold text-slate-900">Perguntas ({missionForm.questions.length}/10)</h3>
                        {missionForm.questions.length < 10 && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleAddQuestion}
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Adicionar
                          </Button>
                        )}
                      </div>

                      {missionForm.questions.map((q, qIndex) => (
                        <div key={qIndex} className="border-2 border-slate-100 rounded-xl p-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="font-outfit font-bold text-slate-700">Pergunta {qIndex + 1}</span>
                            <div className="flex items-center gap-2">
                              <div className="flex items-center gap-2">
                                <Label className="text-sm">AURA:</Label>
                                <Input
                                  type="number"
                                  data-testid={`question-${qIndex}-xp-input`}
                                  value={q.xp_value}
                                  onChange={(e) => handleQuestionChange(qIndex, 'xp_value', parseInt(e.target.value) || 0)}
                                  className="w-20 h-8"
                                  min={1}
                                  max={100}
                                />
                              </div>
                              {missionForm.questions.length > 1 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRemoveQuestion(qIndex)}
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>

                          {/* Question Type Toggle */}
                          <div className="flex items-center gap-2 mb-2">
                            <button
                              type="button"
                              data-testid={`qtype-mc-${qIndex}`}
                              onClick={() => handleQuestionChange(qIndex, 'question_type', 'multiple_choice')}
                              className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                                (q.question_type || 'multiple_choice') === 'multiple_choice'
                                  ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                              }`}
                            >
                              Objetiva
                            </button>
                            <button
                              type="button"
                              data-testid={`qtype-essay-${qIndex}`}
                              onClick={() => handleQuestionChange(qIndex, 'question_type', 'essay')}
                              className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                                q.question_type === 'essay'
                                  ? 'bg-violet-500 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                              }`}
                            >
                              Dissertativa
                            </button>
                          </div>
                          
                          <Input
                            data-testid={`question-${qIndex}-text-input`}
                            value={q.question}
                            onChange={(e) => handleQuestionChange(qIndex, 'question', e.target.value)}
                            placeholder="Digite a pergunta..."
                            required
                          />

                          {/* Question Image Upload */}
                          <div className="flex items-center gap-2">
                            {q.image_url ? (
                              <div className="relative group">
                                <img
                                  src={q.image_url}
                                  alt={q.image_alt || 'Imagem da questão'}
                                  className="h-20 rounded-lg border border-slate-200 object-cover cursor-pointer"
                                  onClick={() => window.open(q.image_url, '_blank')}
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    handleQuestionChange(qIndex, 'image_url', '');
                                    handleQuestionChange(qIndex, 'image_alt', '');
                                  }}
                                  className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ) : (
                              <label
                                data-testid={`question-${qIndex}-image-upload`}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed border-slate-300 text-xs text-slate-500 hover:border-blue-400 hover:text-blue-500 cursor-pointer transition-colors"
                              >
                                <Image className="w-3.5 h-3.5" />
                                Inserir Imagem
                                <input
                                  type="file"
                                  accept="image/png,image/jpeg,image/gif,image/webp"
                                  className="hidden"
                                  onChange={async (e) => {
                                    const file = e.target.files[0];
                                    if (!file) return;
                                    if (file.size > 5 * 1024 * 1024) { toast.error('Imagem muito grande (max 5MB)'); return; }
                                    const formData = new FormData();
                                    formData.append('file', file);
                                    try {
                                      const resp = await fetch(`${BACKEND_URL}/api/missions/upload-image?alt_text=`, {
                                        method: 'POST', credentials: 'include', body: formData
                                      });
                                      if (resp.ok) {
                                        const data = await resp.json();
                                        handleQuestionChange(qIndex, 'image_url', data.image_url);
                                        toast.success('Imagem anexada!');
                                      } else {
                                        const err = await resp.json();
                                        toast.error(err.detail || 'Erro no upload');
                                      }
                                    } catch (err) { toast.error('Erro ao enviar imagem'); }
                                    e.target.value = '';
                                  }}
                                />
                              </label>
                            )}
                            {q.image_url && (
                              <Input
                                value={q.image_alt || ''}
                                onChange={(e) => handleQuestionChange(qIndex, 'image_alt', e.target.value)}
                                placeholder="Texto alternativo (acessibilidade)..."
                                className="flex-1 h-8 text-xs"
                                data-testid={`question-${qIndex}-image-alt`}
                              />
                            )}
                          </div>

                          {/* Multiple Choice Options */}
                          {(q.question_type || 'multiple_choice') === 'multiple_choice' && (
                            <>
                              <div className="grid grid-cols-1 gap-2">
                                {['A', 'B', 'C', 'D'].map((opt) => (
                                  <div key={opt} className="flex items-center gap-2">
                                    <input
                                      type="radio"
                                      name={`correct-${qIndex}`}
                                      checked={q.correct_answer === opt}
                                      onChange={() => handleQuestionChange(qIndex, 'correct_answer', opt)}
                                      className="w-4 h-4 text-purple-600"
                                    />
                                    <span className="w-8 h-8 bg-slate-100 rounded flex items-center justify-center font-bold text-sm">
                                      {opt}
                                    </span>
                                    <Input
                                      data-testid={`question-${qIndex}-option-${opt.toLowerCase()}-input`}
                                      value={q[`option_${opt.toLowerCase()}`] || ''}
                                      onChange={(e) => handleQuestionChange(qIndex, `option_${opt.toLowerCase()}`, e.target.value)}
                                      placeholder={`Alternativa ${opt}${opt === 'C' || opt === 'D' ? ' (opcional)' : ''}...`}
                                      className="flex-1"
                                      required={opt === 'A' || opt === 'B'}
                                    />
                                  </div>
                                ))}
                              </div>
                              <p className="text-xs text-slate-500">Selecione a alternativa correta</p>
                            </>
                          )}

                          {/* Essay: Key Concepts */}
                          {q.question_type === 'essay' && (
                            <div className="p-3 bg-violet-50 rounded-lg border border-violet-100">
                              <Label className="text-xs font-bold text-violet-700 flex items-center gap-1 mb-2">
                                <Target className="w-3 h-3" />
                                Conceitos-Chave (Gabarito de Referência)
                              </Label>
                              <div className="flex flex-wrap gap-1.5 mb-2">
                                {(q.key_concepts || []).map((concept, ci) => (
                                  <span key={ci} className="flex items-center gap-1 px-2 py-0.5 bg-violet-100 text-violet-700 text-xs rounded-full font-bold">
                                    {concept}
                                    <button type="button" onClick={() => {
                                      const newConcepts = [...(q.key_concepts || [])];
                                      newConcepts.splice(ci, 1);
                                      handleQuestionChange(qIndex, 'key_concepts', newConcepts);
                                    }} className="text-violet-400 hover:text-violet-700">
                                      <X className="w-3 h-3" />
                                    </button>
                                  </span>
                                ))}
                              </div>
                              <div className="flex gap-2">
                                <Input
                                  data-testid={`question-${qIndex}-concept-input`}
                                  placeholder="Digite um conceito-chave e pressione Enter..."
                                  className="flex-1 text-xs h-8"
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && e.target.value.trim()) {
                                      e.preventDefault();
                                      handleQuestionChange(qIndex, 'key_concepts', [...(q.key_concepts || []), e.target.value.trim()]);
                                      e.target.value = '';
                                    }
                                  }}
                                />
                              </div>
                              <p className="text-[10px] text-violet-500 mt-1">
                                A IA usará estes conceitos para pré-analisar as respostas dos alunos
                              </p>
                            </div>
                          )}
                          
                          {/* Hint field (Dica de Mestre) */}
                          <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-100">
                            <Label className="text-xs font-medium text-amber-700 flex items-center gap-1 mb-2">
                              <Lightbulb className="w-3 h-3" />
                              Dica de Mestre (opcional)
                            </Label>
                            <Textarea
                              value={q.hint || ''}
                              onChange={(e) => handleQuestionChange(qIndex, 'hint', e.target.value)}
                              placeholder="Uma pista pedagógica que ajuda o aluno a pensar na direção certa..."
                              rows={2}
                              className="text-sm bg-white"
                            />
                            <p className="text-[10px] text-amber-600 mt-1">
                              Esta dica aparecerá se o aluno errar a questão
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="flex justify-between items-center pt-4 border-t border-slate-100">
                      <p className="text-sm text-slate-600">
                        Total: <span className="font-bold text-purple-600">
                          {missionForm.questions.reduce((sum, q) => sum + (q.xp_value || 0), 0)} AURA
                        </span>
                      </p>
                      <Button
                        type="submit"
                        data-testid="submit-mission-btn"
                        className="bg-purple-600 hover:bg-purple-700 text-white"
                      >
                        Criar Missão
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
              </div>
            </div>

            {/* Mission List */}
            {missions.length === 0 ? (
              <div className="bg-white rounded-2xl border-2 border-slate-100 p-12 text-center">
                <Target className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 font-nunito">Nenhuma missão criada ainda</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Active/Expired Tabs */}
                <div className="flex gap-2 mb-4">
                  <Button
                    variant={!archivedMissionsOpen ? "default" : "outline"}
                    size="sm"
                    onClick={() => setArchivedMissionsOpen(false)}
                  >
                    Ativas ({missions.filter(m => m.status !== 'archived').length})
                  </Button>
                  <Button
                    variant={archivedMissionsOpen ? "default" : "outline"}
                    size="sm"
                    onClick={() => setArchivedMissionsOpen(true)}
                  >
                    <Archive className="w-4 h-4 mr-1" />
                    Arquivadas ({missions.filter(m => m.status === 'archived').length})
                  </Button>
                </div>

                {missions
                  .filter(m => archivedMissionsOpen ? m.status === 'archived' : m.status !== 'archived')
                  .map((mission) => {
                    const isExpired = mission.status === 'expired' || (mission.expiration_info?.expired);
                    const isUrgent = mission.expiration_info?.is_urgent && !isExpired;
                    const isArchived = mission.status === 'archived';
                    const completionStats = mission.completion_stats || {};
                    
                    return (
                  <div 
                    key={mission.mission_id} 
                    data-testid="mission-card" 
                    className={`rounded-2xl border-2 p-6 shadow-sm transition-all ${
                      isArchived ? 'bg-slate-100 border-slate-200 opacity-75' :
                      isExpired ? 'border-red-200 bg-red-50/30' : 
                      isUrgent ? 'border-orange-300 bg-orange-50/30' : 
                      'bg-white border-slate-100'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Target className={`w-5 h-5 ${isArchived ? 'text-slate-400' : isExpired ? 'text-red-500' : 'text-purple-600'}`} />
                          <h3 className={`font-outfit font-bold ${isArchived ? 'text-slate-500' : 'text-slate-900'}`}>{mission.title}</h3>
                          {isArchived && (
                            <span className="px-2 py-0.5 bg-slate-200 text-slate-600 text-xs font-bold rounded-full">
                              ARQUIVADA
                            </span>
                          )}
                          {isExpired && !isArchived && (
                            <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs font-bold rounded-full">
                              EXPIRADA
                            </span>
                          )}
                          {isUrgent && !isExpired && (
                            <span className="px-2 py-0.5 bg-orange-100 text-orange-600 text-xs font-bold rounded-full animate-pulse">
                              ⏰ Expira em breve!
                            </span>
                          )}
                        </div>
                        {mission.description && (
                          <p className="text-slate-600 font-nunito mb-2">{mission.description}</p>
                        )}
                        <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
                          <span>{(mission.questions || []).length} {(mission.questions || []).length === 1 ? 'pergunta' : 'perguntas'}</span>
                          
                          {/* Expiration info */}
                          {mission.expires_at && (
                            <span className={`flex items-center gap-1 ${isExpired ? 'text-red-500' : isUrgent ? 'text-orange-500 font-semibold' : ''}`}>
                              <Clock className="w-4 h-4" />
                              {isExpired ? 'Expirou em: ' : 'Expira em: '}
                              {new Date(mission.expires_at).toLocaleDateString('pt-BR', { 
                                day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' 
                              })}
                            </span>
                          )}
                          
                          {/* Completion stats */}
                          {completionStats.total_students > 0 && (
                            <span className="flex items-center gap-1 text-green-600">
                              ✅ {completionStats.completed_count}/{completionStats.total_students} ({completionStats.completion_rate}%)
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="bg-purple-100 text-purple-700 px-4 py-2 rounded-xl font-bold">
                          até {mission.total_xp} AURA
                        </div>
                        {(mission.coins_per_question > 0 || mission.coin_reward > 0) && (
                          <div className="bg-amber-100 text-amber-700 px-3 py-2 rounded-xl font-bold flex items-center gap-1">
                            <Coins className="w-3.5 h-3.5" />
                            {mission.coins_per_question > 0 ? `${mission.coins_per_question}/q` : mission.coin_reward}
                          </div>
                        )}
                        
                        {/* Extend buttons for expired/urgent missions */}
                        {(isExpired || isUrgent) && mission.status !== 'archived' && (
                          <div className="flex gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleExtendMission(mission.mission_id, 24)}
                              className="text-orange-600 border-orange-300 hover:bg-orange-50"
                              title="Prorrogar 24 horas"
                            >
                              <RotateCcw className="w-4 h-4 mr-1" />
                              +24h
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleExtendMission(mission.mission_id, 48)}
                              className="text-orange-600 border-orange-300 hover:bg-orange-50"
                              title="Prorrogar 48 horas"
                            >
                              +48h
                            </Button>
                          </div>
                        )}
                        
                        {/* View details button */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setViewingMission(mission); setViewMissionDialogOpen(true); }}
                          className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                          title="Ver detalhes"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        
                        {/* Report button */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openMissionReport(mission)}
                          className="text-green-500 hover:text-green-700 hover:bg-green-50"
                          title="Relatório de progresso"
                          data-testid={`mission-report-btn-${mission.mission_id}`}
                        >
                          <BarChart2 className="w-4 h-4" />
                        </Button>

                        {/* Essay correction button - show if mission has essay questions */}
                        {mission.questions?.some(q => q.question_type === 'essay') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEssayCorrection(mission.mission_id)}
                            className="text-violet-500 hover:text-violet-700 hover:bg-violet-50"
                            title="Corrigir Dissertativas"
                            data-testid={`essay-correct-btn-${mission.mission_id}`}
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                        )}

                        {/* Release Gabarito button — only if mission is shuffle-enabled, not yet released, and has expiry */}
                        {mission.shuffle_enabled && !mission.gabarito_released_at && mission.expires_at && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleReleaseGabarito(mission)}
                            className="text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50"
                            title="Liberar Gabarito Agora"
                            data-testid={`release-gabarito-btn-${mission.mission_id}`}
                          >
                            <Unlock className="w-4 h-4" />
                          </Button>
                        )}

                        {/* Close Mission button — only for active (non-archived, non-expired) missions */}
                        {!isExpired && !isArchived && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCloseMission(mission)}
                            className="text-rose-500 hover:text-rose-700 hover:bg-rose-50"
                            title="Encerrar Missão Agora"
                            data-testid={`close-mission-btn-${mission.mission_id}`}
                          >
                            <Ban className="w-4 h-4" />
                          </Button>
                        )}

                        {mission.gabarito_released_at && (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100 px-2 py-1 rounded-full"
                            title="Gabarito liberado"
                            data-testid={`gabarito-released-badge-${mission.mission_id}`}
                          >
                            <Unlock className="w-3 h-3" /> Liberado
                          </span>
                        )}
                        
                        {/* Edit button - only for non-archived */}
                        {mission.status !== 'archived' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditMissionDialog(mission)}
                            className="text-purple-500 hover:text-purple-700 hover:bg-purple-50"
                            title="Editar missão"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                        )}
                        
                        {/* Archive/Unarchive button */}
                        {mission.status !== 'archived' ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleArchiveMission(mission.mission_id)}
                            className="text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                            title="Arquivar missão"
                          >
                            <Archive className="w-4 h-4" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openUnarchiveDialog(mission)}
                            className="text-green-500 hover:text-green-700 hover:bg-green-50"
                            title="Reativar missão"
                          >
                            <RotateCcw className="w-4 h-4" />
                          </Button>
                        )}
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid="clone-mission-btn"
                          onClick={() => openCloneMissionDialog(mission)}
                          className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                          title="Clonar para outra turma"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid="delete-mission-btn"
                          onClick={() => {
                            setDeletingMission(mission);
                            setDeleteMissionDialogOpen(true);
                          }}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                    );
                  })}
              </div>
            )}
          </TabsContent>

          {/* ===== LABORATÓRIO DE PESQUISA TAB ===== */}
          <TabsContent value="lab">
            <div className="space-y-6">
              {/* Upload Form */}
              <div className="bg-white rounded-2xl border-2 border-teal-100 p-6 shadow-sm" data-testid="lab-upload-form">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-xl flex items-center justify-center shadow-md">
                    <Upload className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-outfit font-bold text-slate-900">Enviar Material</h3>
                    <p className="text-xs text-slate-500 font-nunito">PDF, Word, Imagem ou Link</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label className="text-sm font-bold text-slate-700 mb-1.5 block">Título *</Label>
                    <Input
                      data-testid="lab-upload-title"
                      placeholder="Ex: Artigo sobre fotossíntese"
                      value={labUploadForm.title}
                      onChange={(e) => setLabUploadForm(prev => ({ ...prev, title: e.target.value }))}
                      className="rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-bold text-slate-700 mb-1.5 block">Área de Estudo</Label>
                    <Input
                      data-testid="lab-upload-area"
                      placeholder="Ex: Biologia, Química, História"
                      value={labUploadForm.area}
                      onChange={(e) => setLabUploadForm(prev => ({ ...prev, area: e.target.value }))}
                      className="rounded-xl"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-4 mb-4">
                  <Label className="text-sm font-bold text-slate-700">Tipo:</Label>
                  <div className="flex gap-2">
                    <Button
                      data-testid="lab-type-file"
                      size="sm"
                      variant={labUploadForm.material_type === 'file' ? 'default' : 'outline'}
                      onClick={() => setLabUploadForm(prev => ({ ...prev, material_type: 'file' }))}
                      className="rounded-xl text-xs"
                    >
                      <FileText className="w-3.5 h-3.5 mr-1" /> Arquivo
                    </Button>
                    <Button
                      data-testid="lab-type-link"
                      size="sm"
                      variant={labUploadForm.material_type === 'link' ? 'default' : 'outline'}
                      onClick={() => setLabUploadForm(prev => ({ ...prev, material_type: 'link' }))}
                      className="rounded-xl text-xs"
                    >
                      <Link2 className="w-3.5 h-3.5 mr-1" /> Link
                    </Button>
                  </div>
                </div>
                {labUploadForm.material_type === 'file' ? (
                  <div className="mb-4">
                    <input
                      data-testid="lab-file-input"
                      type="file"
                      accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp"
                      onChange={(e) => setLabFile(e.target.files[0] || null)}
                      className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-sm file:font-bold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100"
                    />
                    {labFile && <p className="mt-1 text-xs text-slate-400 font-nunito">{labFile.name} ({(labFile.size / 1024 / 1024).toFixed(1)} MB)</p>}
                  </div>
                ) : (
                  <div className="mb-4">
                    <Input
                      data-testid="lab-link-input"
                      placeholder="https://..."
                      value={labUploadForm.link_url}
                      onChange={(e) => setLabUploadForm(prev => ({ ...prev, link_url: e.target.value }))}
                      className="rounded-xl"
                    />
                  </div>
                )}
                <Button
                  data-testid="lab-upload-btn"
                  onClick={handleLabUpload}
                  disabled={labUploading}
                  className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white font-bold rounded-xl"
                >
                  {labUploading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                  {labUploading ? 'Enviando...' : 'Enviar Material'}
                </Button>
              </div>

              {/* Materials List */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-testid="lab-materials-list">
                <h3 className="text-lg font-outfit font-bold text-slate-900 mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-teal-600" />
                  Materiais Enviados ({labMaterials.length})
                </h3>
                {labMaterials.length === 0 ? (
                  <p className="text-sm text-slate-400 font-nunito text-center py-8">Nenhum material enviado ainda.</p>
                ) : (
                  <div className="space-y-3">
                    {labMaterials.map(mat => (
                      <div key={mat.material_id} className={`flex items-center justify-between p-4 rounded-xl border ${mat.visible !== false ? 'border-slate-100 bg-white' : 'border-red-100 bg-red-50/30'}`} data-testid="lab-material-row">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-9 h-9 rounded-lg bg-slate-50 flex items-center justify-center flex-shrink-0">
                            {mat.material_type === 'link' ? <Link2 className="w-4 h-4 text-blue-500" /> :
                             mat.file_type === 'pdf' ? <FileText className="w-4 h-4 text-red-500" /> :
                             <Image className="w-4 h-4 text-purple-500" />}
                          </div>
                          <div className="min-w-0">
                            <p className="font-outfit font-bold text-sm text-slate-800 truncate">{mat.title}</p>
                            <p className="text-xs text-slate-400 font-nunito">{mat.area || 'Geral'} &middot; {mat.material_type === 'link' ? 'Link' : (mat.file_type || '').toUpperCase()}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            data-testid="lab-open-material"
                            onClick={() => {
                              if (mat.material_type === 'link') {
                                window.open(mat.link_url, '_blank');
                              } else {
                                window.open(`${BACKEND_URL}/api/lab/file/${mat.material_id}`, '_blank');
                              }
                            }}
                            className="p-1.5 rounded-lg text-blue-500 hover:bg-blue-50 transition-colors"
                            title="Abrir material"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </button>
                          <button
                            data-testid="lab-toggle-visibility"
                            onClick={() => toggleLabVisibility(mat.material_id, mat.visible !== false)}
                            className={`p-1.5 rounded-lg transition-colors ${mat.visible !== false ? 'text-teal-600 hover:bg-teal-50' : 'text-red-400 hover:bg-red-50'}`}
                            title={mat.visible !== false ? 'Ocultar' : 'Tornar visível'}
                          >
                            {mat.visible !== false ? <Eye className="w-4 h-4" /> : <Eye className="w-4 h-4 opacity-40" />}
                          </button>
                          <button
                            data-testid="lab-delete-material"
                            onClick={() => deleteLabMaterial(mat.material_id)}
                            className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 transition-colors"
                            title="Excluir"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Ranking de Pesquisadores */}
              {labAnalytics && (
                <div className="bg-white rounded-2xl border-2 border-amber-100 p-6 shadow-sm" data-testid="lab-ranking">
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center shadow-md">
                        <Trophy className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-outfit font-bold text-slate-900">Ranking de Pesquisadores</h3>
                        <p className="text-xs text-slate-500 font-nunito">Tempo total de estudo por aluno</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        data-testid="lab-reward-coins-input"
                        type="number"
                        min="1"
                        max="100"
                        value={labRewardCoins}
                        onChange={(e) => setLabRewardCoins(parseInt(e.target.value) || 10)}
                        className="w-20 rounded-xl text-center text-sm font-bold"
                      />
                      <Button
                        data-testid="lab-reward-top5-btn"
                        onClick={rewardTopResearchers}
                        disabled={labRewarding || !labAnalytics?.ranking?.length}
                        size="sm"
                        className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-bold rounded-xl"
                      >
                        {labRewarding ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Award className="w-3.5 h-3.5 mr-1" />}
                        Top 5 +Coins
                      </Button>
                    </div>
                  </div>
                  {labAnalytics.ranking?.length === 0 ? (
                    <p className="text-sm text-slate-400 font-nunito text-center py-4">Nenhum dado de leitura ainda.</p>
                  ) : (
                    <div className="space-y-2">
                      {labAnalytics.ranking.map((r, idx) => (
                        <div key={r.student_id} className={`flex items-center justify-between p-3 rounded-xl ${idx < 5 ? 'bg-amber-50 border border-amber-200' : 'bg-slate-50 border border-slate-100'}`} data-testid="lab-ranking-row">
                          <div className="flex items-center gap-3">
                            <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black ${
                              idx === 0 ? 'bg-amber-400 text-white' : idx === 1 ? 'bg-slate-300 text-white' : idx === 2 ? 'bg-orange-400 text-white' : 'bg-slate-100 text-slate-500'
                            }`}>
                              {idx + 1}
                            </span>
                            <span className="font-outfit font-bold text-sm text-slate-800">{r.name}</span>
                          </div>
                          <div className="flex items-center gap-4 text-xs font-nunito">
                            <span className="text-slate-500"><Clock className="w-3 h-3 inline mr-0.5" />{formatLabTime(r.total_seconds)}</span>
                            <span className="text-teal-600 font-bold">{r.materials_read} lidos</span>
                            <span className="text-emerald-600 font-bold">{r.completed} concluídos{r.completed > 0 ? ` (+${r.completed * 10} AURA)` : ''}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Per-material stats */}
              {labAnalytics?.materials?.length > 0 && (
                <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-testid="lab-material-stats">
                  <h3 className="text-lg font-outfit font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <BarChart2 className="w-5 h-5 text-indigo-500" />
                    Detalhes por Material
                  </h3>
                  <div className="space-y-4">
                    {labAnalytics.materials.map(ms => (
                      <div key={ms.material_id} className="border border-slate-100 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-outfit font-bold text-sm text-slate-800">{ms.title}</p>
                          <span className="text-xs text-slate-400 font-nunito">{ms.total_readers} leitor{ms.total_readers !== 1 ? 'es' : ''}</span>
                        </div>
                        {ms.readers.length > 0 ? (
                          <div className="space-y-1">
                            {ms.readers.map(rd => (
                              <div key={rd.student_id} className="flex items-center justify-between text-xs py-1 px-2 rounded-lg bg-slate-50">
                                <span className="font-nunito text-slate-700">{rd.name}</span>
                                <div className="flex items-center gap-3">
                                  <span className="text-slate-500">{formatLabTime(rd.time_seconds)}</span>
                                  {rd.completed ? (
                                    <span className="text-emerald-600 font-bold flex items-center gap-0.5"><Check className="w-3 h-3" /> Concluído (+10 AURA)</span>
                                  ) : (
                                    <span className="text-amber-500 font-bold">Em estudo</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-slate-400 font-nunito">Ninguém leu ainda.</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

        </Tabs>
      </div>

      {/* Essay Correction Dialog */}
      <Dialog open={essayDialogOpen} onOpenChange={(open) => { setEssayDialogOpen(open); if (!open) { setGradingEssay(null); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <FileText className="w-5 h-5 text-violet-600" />
              Correção de Dissertativas
            </DialogTitle>
          </DialogHeader>

          {loadingEssays ? (
            <div className="flex justify-center py-8"><RefreshCw className="w-6 h-6 animate-spin text-violet-500" /></div>
          ) : gradingEssay ? (
            /* Grading individual submission */
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-outfit font-bold text-lg text-slate-800">{gradingEssay.student_name}</h3>
                <Button variant="ghost" size="sm" onClick={() => setGradingEssay(null)}>
                  <ArrowLeft className="w-4 h-4 mr-1" /> Voltar
                </Button>
              </div>

              {gradingEssay.answers.map((ans) => {
                const signalColors = { green: 'bg-emerald-100 border-emerald-300 text-emerald-800', yellow: 'bg-amber-100 border-amber-300 text-amber-800', red: 'bg-red-100 border-red-300 text-red-800' };
                const signalLabels = { green: 'Domínio', yellow: 'Parcial', red: 'Insuficiente' };
                const signalEmoji = { green: '🟢', yellow: '🟡', red: '🔴' };
                const edit = gradeEdits[ans.question_index] || {};

                return (
                  <div key={ans.question_index} className="border rounded-xl p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold text-sm text-slate-800">Q{ans.question_index + 1}: {ans.question_text}</p>
                        {ans.bloom_level && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-bold">{ans.bloom_level}</span>
                        )}
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold border ${signalColors[ans.ai_signal]}`} data-testid={`signal-${ans.question_index}`}>
                        {signalEmoji[ans.ai_signal]} {signalLabels[ans.ai_signal]}
                      </span>
                    </div>

                    {/* Student answer */}
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 font-bold mb-1">Resposta do Aluno:</p>
                      <p className="text-sm text-slate-700">{ans.student_text || 'Sem resposta'}</p>
                      {ans.student_image_url && (
                        <div className="mt-2">
                          <img src={ans.student_image_url} alt="Anexo do aluno" className="max-h-40 rounded-lg border border-slate-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => window.open(ans.student_image_url, '_blank')} />
                        </div>
                      )}
                    </div>

                    {/* Concepts analysis */}
                    <div className="flex gap-3 flex-wrap">
                      {ans.concepts_found?.length > 0 && (
                        <div>
                          <p className="text-[10px] text-emerald-600 font-bold mb-1">Conceitos encontrados:</p>
                          <div className="flex gap-1 flex-wrap">
                            {ans.concepts_found.map((c, i) => <span key={i} className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] rounded-full font-bold">{c}</span>)}
                          </div>
                        </div>
                      )}
                      {ans.concepts_missing?.length > 0 && (
                        <div>
                          <p className="text-[10px] text-red-500 font-bold mb-1">Conceitos faltantes:</p>
                          <div className="flex gap-1 flex-wrap">
                            {ans.concepts_missing.map((c, i) => <span key={i} className="px-2 py-0.5 bg-red-100 text-red-600 text-[10px] rounded-full font-bold">{c}</span>)}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Grade controls */}
                    <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-100">
                      <div>
                        <Label className="text-xs font-bold text-slate-600">Nota (0-100)</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number" min="0" max="100"
                            value={edit.score ?? 50}
                            onChange={(e) => setGradeEdits(prev => ({ ...prev, [ans.question_index]: { ...prev[ans.question_index], score: parseInt(e.target.value) || 0 } }))}
                            className="w-20 h-8 text-center font-bold"
                            data-testid={`grade-score-${ans.question_index}`}
                          />
                          <Button variant="ghost" size="sm" className="text-xs text-blue-500"
                            onClick={() => setGradeEdits(prev => ({ ...prev, [ans.question_index]: { ...prev[ans.question_index], score: ans.ai_score } }))}>
                            Usar IA ({ans.ai_score})
                          </Button>
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs font-bold text-slate-600">Feedback para o Aluno</Label>
                        <textarea
                          value={edit.feedback ?? ''}
                          onChange={(e) => setGradeEdits(prev => ({ ...prev, [ans.question_index]: { ...prev[ans.question_index], feedback: e.target.value } }))}
                          className="w-full p-2 text-xs border rounded-lg resize-none h-16"
                          placeholder="Feedback pedagógico..."
                          data-testid={`grade-feedback-${ans.question_index}`}
                        />
                        {ans.ai_feedback && !edit.feedback && (
                          <button className="text-[10px] text-blue-500 hover:underline" onClick={() => setGradeEdits(prev => ({ ...prev, [ans.question_index]: { ...prev[ans.question_index], feedback: ans.ai_feedback } }))}>
                            Usar sugestão da IA
                          </button>
                        )}
                        {/* Gabarito image */}
                        <div className="mt-2">
                          {edit.teacher_image_url ? (
                            <div className="relative group inline-block">
                              <img src={edit.teacher_image_url} alt="Gabarito" className="h-16 rounded-lg border" />
                              <button onClick={() => setGradeEdits(prev => ({ ...prev, [ans.question_index]: { ...prev[ans.question_index], teacher_image_url: null } }))} className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] flex items-center justify-center opacity-0 group-hover:opacity-100"><X className="w-2.5 h-2.5" /></button>
                            </div>
                          ) : (
                            <label className="flex items-center gap-1 px-2 py-1 rounded-lg border border-dashed border-slate-300 text-[10px] text-slate-500 hover:border-blue-400 cursor-pointer w-fit">
                              <Image className="w-3 h-3" /> Anexar Gabarito
                              <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                                const file = e.target.files[0];
                                if (!file) return;
                                const formData = new FormData();
                                formData.append('file', file);
                                try {
                                  const resp = await fetch(`${BACKEND_URL}/api/missions/upload-image`, { method: 'POST', credentials: 'include', body: formData });
                                  if (resp.ok) { const d = await resp.json(); setGradeEdits(prev => ({ ...prev, [ans.question_index]: { ...prev[ans.question_index], teacher_image_url: d.image_url } })); }
                                } catch { /* silent */ }
                                e.target.value = '';
                              }} />
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              <Button
                data-testid="finalize-grade-btn"
                onClick={submitGrade}
                disabled={submittingGrade}
                className="w-full bg-gradient-to-r from-violet-500 to-purple-600 text-white font-bold h-11 rounded-xl"
              >
                {submittingGrade ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Finalizar Avaliação e Liberar AURA/Coins
              </Button>
            </div>
          ) : (
            /* Submissions list */
            <div className="space-y-4">
              {essaySummary && (
                <div className="grid grid-cols-5 gap-2">
                  <div className="bg-emerald-50 p-2 rounded-lg text-center"><span className="text-lg font-bold text-emerald-600">{essaySummary.green}</span><p className="text-[10px] text-emerald-700">Domínio</p></div>
                  <div className="bg-amber-50 p-2 rounded-lg text-center"><span className="text-lg font-bold text-amber-600">{essaySummary.yellow}</span><p className="text-[10px] text-amber-700">Parcial</p></div>
                  <div className="bg-red-50 p-2 rounded-lg text-center"><span className="text-lg font-bold text-red-600">{essaySummary.red}</span><p className="text-[10px] text-red-700">Insuficiente</p></div>
                  <div className="bg-green-50 p-2 rounded-lg text-center"><span className="text-lg font-bold text-green-600">{essaySummary.graded}</span><p className="text-[10px] text-green-700">Avaliados</p></div>
                  <div className="bg-orange-50 p-2 rounded-lg text-center"><span className="text-lg font-bold text-orange-600">{essaySummary.pending}</span><p className="text-[10px] text-orange-700">Pendentes</p></div>
                </div>
              )}
              {(!essaySubmissions || essaySubmissions.length === 0) ? (
                <p className="text-center text-slate-400 py-8 font-nunito">Nenhuma resposta dissertativa recebida.</p>
              ) : (
                <div className="space-y-2">
                  {essaySubmissions.map(sub => (
                    <div key={sub.submission_id} className={`flex items-center justify-between p-3 rounded-xl border ${sub.status === 'graded' ? 'border-green-200 bg-green-50/30' : 'border-slate-200'}`} data-testid={`essay-sub-${sub.submission_id}`}>
                      <div className="flex items-center gap-3">
                        <div className="flex gap-0.5">
                          {sub.answers.map((a, i) => (
                            <span key={i} className={`w-3 h-3 rounded-full ${a.ai_signal === 'green' ? 'bg-emerald-400' : a.ai_signal === 'yellow' ? 'bg-amber-400' : 'bg-red-400'}`} />
                          ))}
                        </div>
                        <span className="font-outfit font-bold text-sm text-slate-800">{sub.student_name}</span>
                        <span className="text-xs text-slate-400">{new Date(sub.submitted_at).toLocaleDateString('pt-BR')}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {sub.status === 'graded' ? (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold">Avaliado</span>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => startGrading(sub)} className="text-violet-600 border-violet-200 hover:bg-violet-50 font-bold text-xs" data-testid={`grade-btn-${sub.submission_id}`}>
                            <Pencil className="w-3 h-3 mr-1" /> Avaliar
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* Delete Mission Dialog */}
      <Dialog open={deleteMissionDialogOpen} onOpenChange={setDeleteMissionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-outfit text-red-600">Excluir Missão</DialogTitle>
            <DialogDescription className="text-slate-600">
              Tem certeza que deseja excluir a missão <strong>"{deletingMission?.title}"</strong>?
              <br /><br />
              <span className="text-red-500 font-semibold">Esta ação não pode ser desfeita.</span>
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setDeleteMissionDialogOpen(false)}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              data-testid="confirm-delete-mission-btn"
              onClick={handleDeleteMission}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Excluir
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Clone Mission Dialog */}
      <Dialog open={cloneMissionDialogOpen} onOpenChange={setCloneMissionDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2 text-blue-600">
              <Copy className="w-5 h-5" />
              Clonar para outra turma
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              Selecione as turmas para onde deseja clonar a missão <strong>"{missionToClone?.title}"</strong>.
            </DialogDescription>
          </DialogHeader>
          
          {teacherClassrooms.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-slate-500">Você não possui outras turmas para clonar esta missão.</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto py-2">
              {teacherClassrooms.map((classroom) => (
                <button
                  key={classroom.classroom_id}
                  data-testid={`clone-classroom-${classroom.classroom_id}`}
                  onClick={() => toggleClassroomSelection(classroom.classroom_id)}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                    selectedClassroomsForClone.includes(classroom.classroom_id)
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 hover:border-blue-300 hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-bold text-slate-900">{classroom.name}</h4>
                      <p className="text-sm text-slate-500">Código: {classroom.access_code}</p>
                    </div>
                    {selectedClassroomsForClone.includes(classroom.classroom_id) && (
                      <Check className="w-5 h-5 text-blue-600" />
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
          
          <div className="flex gap-3 mt-4">
            <Button
              variant="outline"
              onClick={handleCancelCloneMission}
              className="flex-1"
              disabled={isCloningMission}
            >
              Cancelar
            </Button>
            <Button
              data-testid="confirm-clone-mission-btn"
              onClick={handleCloneMission}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              disabled={isCloningMission || selectedClassroomsForClone.length === 0}
            >
              {isCloningMission ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                  Clonando...
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Clonar ({selectedClassroomsForClone.length})
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Remove Student Confirmation Dialog */}
      <Dialog open={removeStudentDialogOpen} onOpenChange={setRemoveStudentDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-6 h-6" />
              Remover Aluno
            </DialogTitle>
            <DialogDescription className="text-slate-600 space-y-3 pt-2">
              <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
                <p className="font-bold text-red-800 mb-2 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" /> Exclusão Permanente!
                </p>
                <p className="text-red-700">
                  Você vai excluir <strong className="text-slate-900">PERMANENTEMENTE</strong> o aluno <strong className="text-slate-900">{studentToRemove?.user_name}</strong> da plataforma.
                </p>
                <ul className="text-red-700 text-sm list-disc ml-5 mt-2 space-y-0.5">
                  <li>Pokémon, AURA, nível e evoluções serão apagados</li>
                  <li>Inventário, missões e duelos serão removidos</li>
                  <li>O aluno some do ranking, mural e listas de todos</li>
                  <li>A conta dele é desativada se esta era a única turma</li>
                </ul>
              </div>
              <p className="text-red-600 font-semibold text-center">
                Esta ação NÃO pode ser desfeita.
              </p>
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <Button
              variant="outline"
              onClick={handleCancelRemoveStudent}
              className="flex-1"
              disabled={isRemovingStudent}
            >
              Cancelar
            </Button>
            <Button
              data-testid="confirm-remove-student-btn"
              onClick={handleConfirmRemoveStudent}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              disabled={isRemovingStudent}
            >
              {isRemovingStudent ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                  Removendo...
                </>
              ) : (
                <>
                  <UserMinus className="w-4 h-4 mr-2" />
                  Confirmar Remoção
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Transfer Student Dialog */}
      <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <DialogContent className="max-w-md" data-testid="transfer-student-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2 text-cyan-700">
              <Send className="w-5 h-5" />
              Transferir Aluno
            </DialogTitle>
            <DialogDescription className="text-slate-600 pt-2">
              <span className="block bg-cyan-50 border border-cyan-200 rounded-lg p-3">
                <span className="block font-bold text-slate-900">{studentToTransfer?.user_name}</span>
                {studentToTransfer?.monster && (
                  <span className="block text-xs text-slate-600 mt-0.5">
                    Pokémon: {getMonsterDisplayName(studentToTransfer.monster)} • Nível {studentToTransfer.level}
                  </span>
                )}
                <span className="block text-xs text-cyan-700 mt-2 italic">
                  Toda a evolução, AURA, inventário e histórico serão preservados.
                </span>
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <Label className="font-nunito font-bold">Turma de destino</Label>
            {transferError && (
              <div className="rounded-lg bg-red-50 border-2 border-red-300 p-3 text-sm text-red-700 font-medium" data-testid="transfer-error-banner">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{transferError}</span>
                </div>
              </div>
            )}
            {teacherClassrooms.length === 0 ? (
              <p className="text-sm text-slate-500 italic">Nenhuma outra turma disponível.</p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                {teacherClassrooms.map((c) => (
                  <button
                    type="button"
                    key={c.classroom_id}
                    data-testid={`transfer-target-${c.classroom_id}`}
                    onClick={() => setTransferTargetClassroomId(c.classroom_id)}
                    className={`w-full text-left rounded-xl border-2 p-3 transition-all ${
                      transferTargetClassroomId === c.classroom_id
                        ? 'border-cyan-500 bg-cyan-50 shadow-sm'
                        : 'border-slate-200 bg-white hover:border-cyan-300'
                    }`}
                  >
                    <div className="font-bold text-slate-900">{c.name}</div>
                    <div className="text-xs text-slate-500">Código: {c.access_code}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => setTransferDialogOpen(false)}
              className="flex-1"
              disabled={isTransferringStudent}
            >
              Cancelar
            </Button>
            <Button
              data-testid="confirm-transfer-btn"
              onClick={handleConfirmTransfer}
              className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white"
              disabled={isTransferringStudent || !transferTargetClassroomId || teacherClassrooms.length === 0}
            >
              {isTransferringStudent ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                  Transferindo...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Confirmar Transferência
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Transfer Dialog */}
      <Dialog open={bulkTransferDialogOpen} onOpenChange={setBulkTransferDialogOpen}>
        <DialogContent className="max-w-md" data-testid="bulk-transfer-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2 text-cyan-700">
              <Send className="w-5 h-5" />
              Transferir {selectedStudents.size} Aluno(s)
            </DialogTitle>
            <DialogDescription className="text-slate-600 pt-2">
              <span className="block bg-cyan-50 border border-cyan-200 rounded-lg p-3 text-xs text-cyan-700 italic">
                Toda a evolução, AURA, Pokémon, inventário e histórico de cada aluno serão preservados.
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <Label className="font-nunito font-bold">Turma de destino</Label>
            <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
              {teacherClassrooms.map((c) => (
                <button
                  type="button"
                  key={c.classroom_id}
                  data-testid={`bulk-transfer-target-${c.classroom_id}`}
                  onClick={() => setBulkTransferTargetId(c.classroom_id)}
                  className={`w-full text-left rounded-xl border-2 p-3 transition-all ${
                    bulkTransferTargetId === c.classroom_id
                      ? 'border-cyan-500 bg-cyan-50 shadow-sm'
                      : 'border-slate-200 bg-white hover:border-cyan-300'
                  }`}
                >
                  <div className="font-bold text-slate-900">{c.name}</div>
                  <div className="text-xs text-slate-500">Código: {c.access_code}</div>
                </button>
              ))}
            </div>

            {bulkTransferResult && (
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs space-y-1" data-testid="bulk-transfer-result">
                <div className="font-bold text-slate-700">Resultado:</div>
                <div className="text-emerald-700">✓ {bulkTransferResult.transferred?.length || 0} transferidos</div>
                {bulkTransferResult.skipped?.length > 0 && (
                  <div className="text-amber-700">↺ {bulkTransferResult.skipped.length} pulados (já estavam lá)</div>
                )}
                {bulkTransferResult.errors?.length > 0 && (
                  <div className="text-red-700">
                    ✗ {bulkTransferResult.errors.length} com erro:
                    <ul className="ml-4 list-disc">
                      {bulkTransferResult.errors.slice(0, 5).map((e, i) => (
                        <li key={i}>{e.student_id}: {e.reason}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => setBulkTransferDialogOpen(false)}
              className="flex-1"
              disabled={isBulkTransferring}
            >
              Fechar
            </Button>
            <Button
              data-testid="confirm-bulk-transfer-btn"
              onClick={handleConfirmBulkTransfer}
              className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white"
              disabled={isBulkTransferring || !bulkTransferTargetId}
            >
              {isBulkTransferring ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                  Transferindo...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Confirmar Transferência
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Behavior Dialog */}
      <Dialog open={behaviorDialogOpen} onOpenChange={setBehaviorDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              {selectedBehaviorType === 'positive' ? (
                <>
                  <ThumbsUp className="w-5 h-5 text-green-600" />
                  Comportamento Positivo
                </>
              ) : (
                <>
                  <ThumbsDown className="w-5 h-5 text-red-600" />
                  Comportamento Negativo
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {selectedBehaviorType === 'positive' 
                ? 'Selecione um comportamento positivo para recompensar o aluno com AURA.'
                : 'Selecione um comportamento negativo para penalizar o aluno com HP.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {Object.entries(behaviors[selectedBehaviorType] || {}).map(([position, behavior]) => (
              <button
                key={`behavior-${selectedBehaviorType}-${position}`}
                data-testid={`behavior-option-${position}`}
                onClick={() => handleApplyBehavior(behavior.behavior_id)}
                className={`w-full p-4 rounded-xl border-2 text-left transition-all hover:scale-[1.02] ${
                  selectedBehaviorType === 'positive'
                    ? 'border-green-200 hover:border-green-400 hover:bg-green-50'
                    : 'border-red-200 hover:border-red-400 hover:bg-red-50'
                }`}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="font-bold text-slate-900">{behavior.name}</h4>
                    {behavior.description && (
                      <p className="text-sm text-slate-500">{behavior.description}</p>
                    )}
                  </div>
                  <div className={`px-3 py-1 rounded-lg font-bold text-sm ${
                    selectedBehaviorType === 'positive'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {selectedBehaviorType === 'positive'
                      ? `+${behavior.xp_reward} AURA`
                      : `-${behavior.hp_penalty} HP`}
                  </div>
                </div>
              </button>
            ))}
            {Object.keys(behaviors[selectedBehaviorType] || {}).length === 0 && (
              <div className="text-center py-8 text-slate-500">
                <p>Nenhum comportamento configurado.</p>
                <p className="text-sm">Configure comportamentos na aba Comportamentos do menu principal.</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Pokemon Dialog */}
      <Dialog open={pokemonDialogOpen} onOpenChange={setPokemonDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-blue-600" />
              Trocar Pokemon
            </DialogTitle>
            <DialogDescription>
              Selecione um novo visual para o pokemon de {selectedStudentForPokemon?.user_name}
            </DialogDescription>
          </DialogHeader>
          {loadingGifs ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : Object.keys(availableGifs).length > 0 ? (
            <div className="grid grid-cols-5 gap-3 max-h-80 overflow-y-auto p-2">
              {Object.entries(availableGifs).map(([position, stageData]) => {
                const posNum = parseInt(position);
                const stage = selectedStudentForPokemon?.monster?.stage || 1;
                const gifData = stageData[stage] || stageData[1];
                if (!gifData) return null;
                
                return (
                  <button
                    key={`pokemon-${posNum}`}
                    data-testid={`pokemon-option-${posNum}`}
                    onClick={() => handleChangePokemon(posNum)}
                    className={`p-3 rounded-xl border-2 transition-all hover:scale-105 ${
                      selectedStudentForPokemon?.monster?.monster_index === posNum 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-slate-200 hover:border-blue-300 hover:bg-blue-50'
                    }`}
                  >
                    <img
                      src={buildGifUrl(gifData.url)}
                      alt={`Pokemon ${posNum + 1}`}
                      className="w-16 h-16 object-contain mx-auto"
                    />
                    <p className="text-xs text-center mt-1 font-medium text-slate-600">#{posNum + 1}</p>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <p>Nenhum GIF disponível para esta classe.</p>
              <p className="text-sm">Faça upload de GIFs na página de administração.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* View Mission Dialog */}
      <Dialog open={viewMissionDialogOpen} onOpenChange={setViewMissionDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              {viewingMission?.title}
            </DialogTitle>
            <DialogDescription>
              {viewingMission?.description || 'Sem descrição'}
            </DialogDescription>
          </DialogHeader>
          {viewingMission && (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2 text-sm">
                <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded">
                  {viewingMission.questions?.length || 0} questões
                </span>
                <span className="bg-green-100 text-green-700 px-2 py-1 rounded">
                  até {viewingMission.total_xp} AURA
                </span>
                {viewingMission.expires_at && (
                  <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded">
                    Expira: {new Date(viewingMission.expires_at).toLocaleString('pt-BR')}
                  </span>
                )}
              </div>
              
              {viewingMission.questions?.map((q, idx) => (
                <div key={idx} className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                  <div className="font-semibold text-slate-800 mb-3">
                    {idx + 1}. {q.question}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {['A', 'B', 'C', 'D'].map(opt => {
                      const optValue = q[`option_${opt.toLowerCase()}`];
                      if (!optValue) return null;
                      const isCorrect = q.correct_answer?.toUpperCase() === opt;
                      return (
                        <div 
                          key={opt}
                          className={`p-2 rounded-lg text-sm ${
                            isCorrect 
                              ? 'bg-green-100 border-2 border-green-400 text-green-800 font-semibold' 
                              : 'bg-white border border-slate-200'
                          }`}
                        >
                          <span className="font-bold">{opt})</span> {optValue}
                          {isCorrect && <span className="ml-2">✓</span>}
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-2 text-xs text-slate-500 flex gap-4">
                    <span>AURA: {q.xp_value || 10}</span>
                    {q.hint && <span>Dica: {q.hint}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Mission Dialog */}
      <Dialog open={editMissionDialogOpen} onOpenChange={setEditMissionDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5" />
              Editar Missão
            </DialogTitle>
            {completionsWarning > 0 && (
              <div className="bg-orange-50 border border-orange-200 text-orange-800 p-3 rounded-lg flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                <span className="font-semibold">Atenção: {completionsWarning} aluno(s) já responderam esta missão.</span>
              </div>
            )}
          </DialogHeader>
          {editingMission && (
            <div className="space-y-4">
              <div>
                <Label>Título</Label>
                <Input
                  value={editingMission.title}
                  onChange={(e) => setEditingMission(prev => ({ ...prev, title: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Descrição</Label>
                <Textarea
                  value={editingMission.description || ''}
                  onChange={(e) => setEditingMission(prev => ({ ...prev, description: e.target.value }))}
                  className="mt-1"
                  rows={2}
                />
              </div>
              <div>
                <Label className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Data de Expiração
                </Label>
                <Input
                  type="datetime-local"
                  value={isoToLocalInputValue(editingMission.expires_at)}
                  onChange={(e) => setEditingMission(prev => ({ 
                    ...prev, 
                    expires_at: e.target.value ? new Date(e.target.value).toISOString() : ''
                  }))}
                  className="mt-1"
                />
              </div>
              
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Questões</h4>
                {editingMission.questions?.map((q, idx) => (
                  <div key={idx} className="bg-slate-50 rounded-xl p-4 mb-4 border">
                    <div className="font-medium mb-2">Questão {idx + 1}</div>
                    <Textarea
                      value={q.question}
                      onChange={(e) => {
                        const newQuestions = [...editingMission.questions];
                        newQuestions[idx].question = e.target.value;
                        setEditingMission(prev => ({ ...prev, questions: newQuestions }));
                      }}
                      className="mb-2"
                      rows={2}
                    />
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      {['A', 'B', 'C', 'D'].map(opt => (
                        <div key={opt} className="flex items-center gap-2">
                          <span className="font-bold w-6">{opt})</span>
                          <Input
                            value={q[`option_${opt.toLowerCase()}`] || ''}
                            onChange={(e) => {
                              const newQuestions = [...editingMission.questions];
                              newQuestions[idx][`option_${opt.toLowerCase()}`] = e.target.value;
                              setEditingMission(prev => ({ ...prev, questions: newQuestions }));
                            }}
                            className="flex-1"
                          />
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-4">
                      <div className="flex items-center gap-2">
                        <Label>Resposta correta:</Label>
                        <select
                          value={q.correct_answer || 'A'}
                          onChange={(e) => {
                            const newQuestions = [...editingMission.questions];
                            newQuestions[idx].correct_answer = e.target.value;
                            setEditingMission(prev => ({ ...prev, questions: newQuestions }));
                          }}
                          className="border rounded px-2 py-1"
                        >
                          {['A', 'B', 'C', 'D'].map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label>AURA:</Label>
                        <Input
                          type="number"
                          value={q.xp_value || 10}
                          onChange={(e) => {
                            const newQuestions = [...editingMission.questions];
                            newQuestions[idx].xp_value = parseInt(e.target.value) || 10;
                            setEditingMission(prev => ({ ...prev, questions: newQuestions }));
                          }}
                          className="w-20"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditMissionDialogOpen(false)} disabled={savingMission}>
                  Cancelar
                </Button>
                <Button onClick={handleSaveEditedMission} disabled={savingMission}>
                  {savingMission ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Salvando...
                    </>
                  ) : completionsWarning > 0 ? (
                    'Salvar e Recalcular Notas'
                  ) : (
                    'Salvar Alterações'
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Mission Report Dialog */}
      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <BarChart2 className="w-5 h-5" />
                Relatório de Progresso
              </DialogTitle>
              {missionReport && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={exportReportToCSV}
                    className="text-green-600 border-green-200 hover:bg-green-50"
                    data-testid="export-csv-btn"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-1" />
                    CSV
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={exportReportToPDF}
                    className="text-blue-600 border-blue-200 hover:bg-blue-50"
                    data-testid="export-pdf-btn"
                  >
                    <Download className="w-4 h-4 mr-1" />
                    PDF
                  </Button>
                </div>
              )}
            </div>
            <DialogDescription>
              {missionReport?.mission_title}
            </DialogDescription>
          </DialogHeader>
          {loadingReport ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : missionReport && (
            <div className="space-y-4">
              {/* Summary stats */}
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-green-50 p-3 rounded-xl text-center">
                  <div className="text-2xl font-bold text-green-600">{missionReport.completed_count}</div>
                  <div className="text-xs text-green-700">Concluídas</div>
                </div>
                <div className="bg-orange-50 p-3 rounded-xl text-center">
                  <div className="text-2xl font-bold text-orange-600">{missionReport.pending_count}</div>
                  <div className="text-xs text-orange-700">Pendentes</div>
                </div>
                <div className="bg-purple-50 p-3 rounded-xl text-center">
                  <div className="text-2xl font-bold text-purple-600">{missionReport.average_score}%</div>
                  <div className="text-xs text-purple-700">Média Nota</div>
                </div>
                <div className="bg-blue-50 p-3 rounded-xl text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {missionReport.avg_time_seconds > 0
                      ? `${Math.floor(missionReport.avg_time_seconds / 60)}m${(missionReport.avg_time_seconds % 60).toString().padStart(2,'0')}s`
                      : '--'}
                  </div>
                  <div className="text-xs text-blue-700 flex items-center justify-center gap-0.5"><Clock className="w-3 h-3" /> Tempo Médio</div>
                </div>
              </div>

              {/* Critical Question Card */}
              {missionReport.critical_question && (
                <div className="bg-gradient-to-r from-red-50 to-rose-50 border-2 border-red-200 rounded-xl p-4" data-testid="critical-question-card">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-4.5 h-4.5 text-red-500" />
                    <span className="font-outfit font-bold text-sm text-red-800">Questão Crítica (maior índice de erros)</span>
                  </div>
                  <p className="text-sm text-slate-700 font-nunito mb-1">
                    <span className="font-bold text-red-600">Q{missionReport.critical_question.index + 1}:</span> {missionReport.critical_question.question_text}
                  </p>
                  <div className="flex items-center gap-3 text-xs mt-2">
                    <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-bold">
                      {missionReport.critical_question.error_count} erros ({missionReport.critical_question.error_rate}%)
                    </span>
                    <span className="text-slate-500">Resposta correta: <strong>{missionReport.critical_question.correct_answer}</strong></span>
                  </div>
                </div>
              )}

              {/* Error heat map mini */}
              {missionReport.error_counts?.length > 0 && missionReport.completed_count > 0 && (
                <div className="bg-slate-50 rounded-xl p-3">
                  <p className="text-xs font-bold text-slate-600 mb-2">Erros por Questão</p>
                  <div className="flex gap-1.5 flex-wrap">
                    {missionReport.error_counts.map((count, qi) => {
                      const rate = missionReport.completed_count > 0 ? count / missionReport.completed_count : 0;
                      const bg = rate === 0 ? 'bg-green-100 text-green-700' : rate < 0.3 ? 'bg-yellow-100 text-yellow-700' : rate < 0.6 ? 'bg-orange-100 text-orange-700' : 'bg-red-100 text-red-700';
                      return (
                        <div key={qi} className={`w-9 h-9 rounded-lg flex flex-col items-center justify-center ${bg}`} title={`Q${qi+1}: ${count} erros`}>
                          <span className="text-[9px] font-bold">Q{qi+1}</span>
                          <span className="text-[10px] font-black">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Charts Section */}
              <div className="grid grid-cols-2 gap-4">
                {/* Donut Chart - Completion Rate */}
                <div className="bg-slate-50 p-4 rounded-xl" data-testid="completion-chart">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3 text-center">Taxa de Conclusão</h4>
                  <div className="relative w-32 h-32 mx-auto">
                    <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                      {/* Background circle */}
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        fill="none"
                        stroke="#fed7aa"
                        strokeWidth="12"
                      />
                      {/* Progress circle */}
                      <circle
                        cx="50"
                        cy="50"
                        r="40"
                        fill="none"
                        stroke="#16a34a"
                        strokeWidth="12"
                        strokeLinecap="round"
                        strokeDasharray={`${(missionReport.completed_count / missionReport.total_students) * 251.2} 251.2`}
                        className="transition-all duration-500"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-2xl font-bold text-slate-800">
                        {missionReport.total_students > 0 
                          ? Math.round((missionReport.completed_count / missionReport.total_students) * 100) 
                          : 0}%
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-center gap-4 mt-3">
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full bg-green-600"></div>
                      <span className="text-xs text-slate-600">Concluídas</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded-full bg-orange-300"></div>
                      <span className="text-xs text-slate-600">Pendentes</span>
                    </div>
                  </div>
                </div>
                
                {/* Histogram - Score Distribution */}
                <div className="bg-slate-50 p-4 rounded-xl" data-testid="score-histogram">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3 text-center">Distribuição de Notas</h4>
                  {(() => {
                    const completedStudents = missionReport.students.filter(s => s.status === 'completed');
                    if (completedStudents.length === 0) {
                      return (
                        <div className="flex items-center justify-center h-32 text-slate-400 text-sm">
                          Nenhuma nota ainda
                        </div>
                      );
                    }
                    
                    // Calculate score ranges
                    const ranges = [
                      { label: '0-49%', min: 0, max: 49, color: 'bg-red-500', count: 0 },
                      { label: '50-69%', min: 50, max: 69, color: 'bg-orange-500', count: 0 },
                      { label: '70-89%', min: 70, max: 89, color: 'bg-yellow-500', count: 0 },
                      { label: '90-100%', min: 90, max: 100, color: 'bg-green-500', count: 0 }
                    ];
                    
                    completedStudents.forEach(s => {
                      const range = ranges.find(r => s.percentage >= r.min && s.percentage <= r.max);
                      if (range) range.count++;
                    });
                    
                    const maxCount = Math.max(...ranges.map(r => r.count), 1);
                    
                    return (
                      <div className="flex items-end justify-center gap-2 h-28">
                        {ranges.map((range, idx) => (
                          <div key={range.label} className="flex flex-col items-center gap-1">
                            <div 
                              className={`w-10 ${range.color} rounded-t transition-all duration-500`}
                              style={{ 
                                height: `${Math.max((range.count / maxCount) * 80, range.count > 0 ? 8 : 0)}px`,
                                minHeight: range.count > 0 ? '8px' : '0'
                              }}
                            >
                              {range.count > 0 && (
                                <div className="text-xs text-white font-bold text-center pt-0.5">
                                  {range.count}
                                </div>
                              )}
                            </div>
                            <span className="text-[10px] text-slate-500 whitespace-nowrap">{range.label}</span>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>
              
              {/* Filter buttons */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">Filtrar:</span>
                <div className="flex gap-1">
                  <button
                    onClick={() => setReportFilter('all')}
                    className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                      reportFilter === 'all'
                        ? 'bg-slate-800 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    data-testid="report-filter-all"
                  >
                    Todos ({missionReport.total_students})
                  </button>
                  <button
                    onClick={() => setReportFilter('completed')}
                    className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                      reportFilter === 'completed'
                        ? 'bg-green-600 text-white'
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                    data-testid="report-filter-completed"
                  >
                    Concluídas ({missionReport.completed_count})
                  </button>
                  <button
                    onClick={() => setReportFilter('pending')}
                    className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                      reportFilter === 'pending'
                        ? 'bg-orange-600 text-white'
                        : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                    }`}
                    data-testid="report-filter-pending"
                  >
                    Pendentes ({missionReport.pending_count})
                  </button>
                </div>
              </div>
              
              {/* Student list */}
              <div className="border rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-semibold">Aluno</th>
                      <th className="px-2 py-2 text-center text-xs font-semibold">Status</th>
                      <th className="px-2 py-2 text-center text-xs font-semibold">Nota</th>
                      <th className="px-2 py-2 text-center text-xs font-semibold" title="Tempo de conclusão"><Clock className="w-3.5 h-3.5 mx-auto text-slate-500" /></th>
                      <th className="px-2 py-2 text-center text-xs font-semibold" title="Trocas de aba"><Eye className="w-3.5 h-3.5 mx-auto text-slate-500" /></th>
                      <th className="px-2 py-2 text-center text-xs font-semibold">AURA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getFilteredStudents().map((student, idx) => (
                      <tr key={student.student_id} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} ${student.attention_flag ? 'bg-red-50/50' : ''}`}>
                        <td className="px-3 py-2 text-sm">
                          <div>
                          {student.status === 'completed' ? (
                            <button
                              onClick={() => openStudentAnswers(missionReport.mission_id, student.student_id, student.name)}
                              className="text-blue-600 hover:text-blue-800 hover:underline font-medium flex items-center gap-1"
                              data-testid={`view-student-answers-${student.student_id}`}
                            >
                              {student.name}
                              <Eye className="w-3 h-3" />
                            </button>
                          ) : (
                            <span className="text-slate-600">{student.name}</span>
                          )}
                          {student.attention_flag && (
                            <span className="flex items-center gap-0.5 text-[10px] text-red-500 font-bold mt-0.5" data-testid="attention-flag">
                              <AlertTriangle className="w-3 h-3" /> {student.attention_flag}
                            </span>
                          )}
                          </div>
                        </td>
                        <td className="px-2 py-2 text-center">
                          {student.status === 'completed' ? (
                            <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs font-bold">
                              ✓
                            </span>
                          ) : (
                            <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full text-xs font-bold">
                              ...
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-2 text-center text-sm">
                          {student.status === 'completed' ? (
                            <span className={`font-semibold ${student.percentage >= 70 ? 'text-green-600' : student.percentage >= 50 ? 'text-orange-600' : 'text-red-600'}`}>
                              {student.correct_count}/{student.total_questions} ({student.percentage}%)
                            </span>
                          ) : '-'}
                        </td>
                        <td className="px-2 py-2 text-center text-xs text-slate-600 tabular-nums">
                          {student.status === 'completed' && student.time_seconds > 0
                            ? `${Math.floor(student.time_seconds / 60)}:${(student.time_seconds % 60).toString().padStart(2,'0')}`
                            : '-'}
                        </td>
                        <td className="px-2 py-2 text-center text-xs">
                          {student.status === 'completed' ? (
                            <span className={student.tab_switches > 2 ? 'text-red-500 font-bold' : student.tab_switches > 0 ? 'text-amber-500 font-bold' : 'text-slate-400'}>
                              {student.tab_switches || 0}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="px-2 py-2 text-center text-sm">
                          {student.status === 'completed' ? (
                            <span className="text-purple-600 font-semibold">+{student.xp_earned}</span>
                          ) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Student Answers Dialog */}
      <Dialog open={studentAnswersDialogOpen} onOpenChange={setStudentAnswersDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Respostas do Aluno
            </DialogTitle>
            <DialogDescription>
              {studentAnswersData?.student_name} - {studentAnswersData?.mission_title}
            </DialogDescription>
          </DialogHeader>
          {loadingStudentAnswers ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : studentAnswersData && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="flex items-center justify-between bg-slate-50 p-4 rounded-xl">
                <div>
                  <span className="font-semibold text-slate-700">Resultado:</span>
                  <span className={`ml-2 font-bold ${
                    (studentAnswersData.correct_count / studentAnswersData.total_questions) >= 0.7 
                      ? 'text-green-600' 
                      : (studentAnswersData.correct_count / studentAnswersData.total_questions) >= 0.5 
                        ? 'text-orange-600' 
                        : 'text-red-600'
                  }`}>
                    {studentAnswersData.correct_count}/{studentAnswersData.total_questions} acertos
                    ({Math.round((studentAnswersData.correct_count / studentAnswersData.total_questions) * 100)}%)
                  </span>
                </div>
                <div className="text-purple-600 font-bold">
                  +{studentAnswersData.xp_earned} XP
                </div>
              </div>

              {/* Filter buttons */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">Filtrar questões:</span>
                <div className="flex gap-1">
                  <button
                    onClick={() => setAnswersFilter('all')}
                    className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                      answersFilter === 'all'
                        ? 'bg-slate-800 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    data-testid="answers-filter-all"
                  >
                    Todas ({studentAnswersData.total_questions})
                  </button>
                  <button
                    onClick={() => setAnswersFilter('correct')}
                    className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                      answersFilter === 'correct'
                        ? 'bg-green-600 text-white'
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                    data-testid="answers-filter-correct"
                  >
                    Acertos ({studentAnswersData.correct_count})
                  </button>
                  <button
                    onClick={() => setAnswersFilter('wrong')}
                    className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${
                      answersFilter === 'wrong'
                        ? 'bg-red-600 text-white'
                        : 'bg-red-100 text-red-700 hover:bg-red-200'
                    }`}
                    data-testid="answers-filter-wrong"
                  >
                    Erros ({studentAnswersData.total_questions - studentAnswersData.correct_count})
                  </button>
                </div>
              </div>

              {/* Questions and Answers */}
              <div className="space-y-3">
                {getFilteredAnswers().length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    Nenhuma questão encontrada com este filtro.
                  </div>
                ) : getFilteredAnswers().map((q, idx) => (
                  <div 
                    key={`answer-${q.question_index ?? idx}`} 
                    className={`rounded-xl p-4 border-2 ${
                      q.is_correct 
                        ? 'bg-green-50 border-green-200' 
                        : 'bg-red-50 border-red-200'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="font-semibold text-slate-800">
                        {q.question_number}. {q.question_text}
                      </div>
                      <div className={`px-2 py-1 rounded-full text-xs font-bold ${
                        q.is_correct 
                          ? 'bg-green-200 text-green-800' 
                          : 'bg-red-200 text-red-800'
                      }`}>
                        {q.is_correct ? '✓ Correta' : '✗ Errada'}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {['A', 'B', 'C', 'D'].map(opt => {
                        const optValue = q[`option_${opt.toLowerCase()}`];
                        if (!optValue) return null;
                        const isCorrectAnswer = q.correct_answer?.toUpperCase() === opt;
                        const isStudentAnswer = q.student_answer?.toUpperCase() === opt;
                        
                        let bgClass = 'bg-white border-slate-200';
                        if (isCorrectAnswer && isStudentAnswer) {
                          bgClass = 'bg-green-200 border-green-400 ring-2 ring-green-500';
                        } else if (isCorrectAnswer) {
                          bgClass = 'bg-green-100 border-green-300';
                        } else if (isStudentAnswer) {
                          bgClass = 'bg-red-200 border-red-400 ring-2 ring-red-500';
                        }
                        
                        return (
                          <div 
                            key={opt}
                            className={`p-2 rounded-lg text-sm border ${bgClass}`}
                          >
                            <span className="font-bold">{opt})</span> {optValue}
                            {isCorrectAnswer && <span className="ml-2 text-green-700">✓</span>}
                            {isStudentAnswer && !isCorrectAnswer && <span className="ml-2 text-red-700">(escolhida)</span>}
                          </div>
                        );
                      })}
                    </div>
                    
                    <div className="mt-2 text-xs text-slate-500">
                      AURA: {q.xp_value || 10}
                      {q.hint && <span className="ml-4">Dica: {q.hint}</span>}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Completed at */}
              {studentAnswersData.completed_at && (
                <div className="text-center text-sm text-slate-500 pt-2">
                  Completada em: {new Date(studentAnswersData.completed_at).toLocaleString('pt-BR')}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Unarchive Mission Dialog */}
      <Dialog open={unarchiveDialogOpen} onOpenChange={setUnarchiveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reativar Missão</DialogTitle>
            <DialogDescription>
              Deseja reativar "{unarchivingMission?.title}"?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-slate-600">
              Escolha uma opção:
            </p>
            <div className="flex flex-col gap-3">
              <Button
                variant="outline"
                className="justify-start h-auto py-3"
                onClick={() => handleUnarchiveMission(false)}
              >
                <div className="text-left">
                  <div className="font-semibold">Manter notas atuais</div>
                  <div className="text-sm text-slate-500">Alunos que já responderam mantêm suas notas</div>
                </div>
              </Button>
              <Button
                variant="outline"
                className="justify-start h-auto py-3 border-orange-300 hover:bg-orange-50"
                onClick={() => handleUnarchiveMission(true)}
              >
                <div className="text-left">
                  <div className="font-semibold text-orange-700">Resetar progresso</div>
                  <div className="text-sm text-orange-600">Todos os alunos precisarão refazer a missão</div>
                </div>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* AI Mission Generator Modal */}
      {/* Bulk AURA Dialog */}
      <Dialog open={bulkXPDialogOpen} onOpenChange={setBulkXPDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              Atribuir AURA à Turma
            </DialogTitle>
            <DialogDescription>
              Selecione os alunos e o valor de AURA que deseja atribuir
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* AURA Value Input */}
            <div className="space-y-2">
              <Label htmlFor="bulk-xp-amount" className="font-nunito font-semibold">
                Valor de AURA
              </Label>
              <div className="flex items-center gap-3">
                <Input
                  id="bulk-xp-amount"
                  type="number"
                  min="1"
                  placeholder="Ex: 100, 500, 1000..."
                  value={bulkXPAmount}
                  onChange={(e) => setBulkXPAmount(e.target.value)}
                  className="max-w-[200px] text-lg font-bold"
                  data-testid="bulk-xp-amount-input"
                />
                <span className="text-slate-500 font-nunito">AURA</span>
              </div>
            </div>
            
            {/* Reason Input (Optional) */}
            <div className="space-y-2">
              <Label htmlFor="bulk-xp-reason" className="font-nunito font-semibold flex items-center gap-2">
                <span>Motivo</span>
                <span className="text-xs text-slate-400">(opcional)</span>
              </Label>
              <Input
                id="bulk-xp-reason"
                placeholder="Ex: Participação na Feira de Ciências"
                value={bulkXPReason}
                onChange={(e) => setBulkXPReason(e.target.value)}
                className="w-full"
                data-testid="bulk-xp-reason-input"
              />
              <p className="text-xs text-slate-500 font-nunito">
                Este motivo aparecerá na notificação dos alunos
              </p>
            </div>
            
            {/* Student Selection — search + alphabetical listing */}
            {(() => {
              // Derived list rebuilt on every render — cheap for a classroom.
              const q = bulkXPSearchQuery.trim().toLowerCase();
              const visibleStudents = [...students]
                .sort((a, b) =>
                  (a.user_name || '').localeCompare(b.user_name || '', 'pt-BR', { sensitivity: 'base' })
                )
                .filter((s) => !q || (s.user_name || '').toLowerCase().includes(q));
              const visibleIds = visibleStudents.map((s) => s.student_id);
              const allVisibleSelected =
                visibleIds.length > 0 && visibleIds.every((id) => selectedStudentsForXP.has(id));
              const toggleAllVisible = () => {
                setSelectedStudentsForXP((prev) => {
                  const next = new Set(prev);
                  if (allVisibleSelected) {
                    visibleIds.forEach((id) => next.delete(id));
                  } else {
                    visibleIds.forEach((id) => next.add(id));
                  }
                  return next;
                });
              };

              return (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="font-nunito font-semibold">Selecionar Alunos</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="select-all-students"
                        checked={allVisibleSelected}
                        onChange={toggleAllVisible}
                        className="w-4 h-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                        data-testid="select-all-checkbox"
                      />
                      <Label htmlFor="select-all-students" className="text-sm text-slate-600 cursor-pointer">
                        {q ? `Selecionar visíveis (${visibleStudents.length})` : `Selecionar Todos (${students.length})`}
                      </Label>
                    </div>
                  </div>

                  {/* Search input with magnifying-glass icon */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                    <Input
                      type="text"
                      value={bulkXPSearchQuery}
                      onChange={(e) => setBulkXPSearchQuery(e.target.value)}
                      placeholder="Buscar aluno pelo nome..."
                      className="pl-9 pr-9"
                      data-testid="bulk-xp-search-input"
                    />
                    {bulkXPSearchQuery && (
                      <button
                        type="button"
                        onClick={() => setBulkXPSearchQuery('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-600"
                        data-testid="bulk-xp-search-clear"
                        aria-label="Limpar busca"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>

                  <div className="border rounded-xl p-3 max-h-[300px] overflow-y-auto space-y-2 bg-slate-50" data-testid="bulk-xp-student-list">
                    {visibleStudents.length === 0 ? (
                      <p className="text-sm text-slate-500 text-center py-6" data-testid="bulk-xp-empty">
                        Nenhum aluno encontrado com "{bulkXPSearchQuery}"
                      </p>
                    ) : (
                      visibleStudents.map((student) => (
                        <div
                          key={student.student_id}
                          className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                            selectedStudentsForXP.has(student.student_id)
                              ? 'bg-purple-100 border-2 border-purple-300'
                              : 'bg-white border-2 border-transparent hover:border-slate-200'
                          }`}
                          onClick={() => handleToggleStudentSelection(student.student_id)}
                          data-testid={`student-select-${student.student_id}`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedStudentsForXP.has(student.student_id)}
                            onChange={() => handleToggleStudentSelection(student.student_id)}
                            className="w-4 h-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                            onClick={(e) => e.stopPropagation()}
                          />

                          {/* Student Avatar */}
                          <div className="w-10 h-10 rounded-full overflow-hidden bg-slate-200 flex-shrink-0">
                            {student.monster ? (
                              <img
                                src={getMonsterGifByMonster(student.monster)}
                                alt=""
                                loading="lazy"
                                className="w-full h-full object-contain"
                                onError={(e) => { if (!e.currentTarget.dataset.fb) { e.currentTarget.dataset.fb = '1'; e.currentTarget.src = getMonsterFallbackGif(student.monster); } }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-lg">🥚</div>
                            )}
                          </div>

                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-slate-800 truncate">{student.user_name}</p>
                            <p className="text-xs text-slate-500">
                              Nível {student.level || 1} • {student.xp || 0} AURA
                            </p>
                          </div>

                          {selectedStudentsForXP.has(student.student_id) && (
                            <Check className="w-5 h-5 text-purple-600 flex-shrink-0" />
                          )}
                        </div>
                      ))
                    )}
                  </div>

                  <p className="text-sm text-slate-500">
                    {selectedStudentsForXP.size} aluno{selectedStudentsForXP.size !== 1 ? 's' : ''} selecionado{selectedStudentsForXP.size !== 1 ? 's' : ''}
                  </p>
                </div>
              );
            })()}
          </div>
          
          <DialogFooter className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setBulkXPDialogOpen(false);
                setSelectedStudentsForXP(new Set());
                setBulkXPAmount('');
                setBulkXPReason('');
                setBulkXPSearchQuery('');
              }}
              disabled={isApplyingBulkXP}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleApplyBulkXP}
              disabled={isApplyingBulkXP || selectedStudentsForXP.size === 0 || !bulkXPAmount}
              className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700"
              data-testid="apply-bulk-xp-btn"
            >
              {isApplyingBulkXP ? (
                <span className="flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Atribuindo...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Atribuir AURA
                </span>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AIMissionGeneratorModal
        isOpen={aiGeneratorOpen}
        onClose={() => setAiGeneratorOpen(false)}
        onMissionGenerated={handleAIMissionGenerated}
      />

      {/* Create/Edit Group Dialog */}
      <Dialog open={createGroupDialogOpen} onOpenChange={(open) => {
        setCreateGroupDialogOpen(open);
        if (!open) {
          setEditingGroup(null);
          setNewGroupName('');
          setNewGroupColor('#6366f1');
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">
              {editingGroup ? 'Editar Grupo' : 'Criar Novo Grupo'}
            </DialogTitle>
            <DialogDescription>
              {editingGroup ? 'Altere o nome ou cor do grupo.' : 'Defina o nome e a cor da equipe.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="group-name" className="text-sm font-bold text-slate-700">Nome do Grupo</Label>
              <Input
                id="group-name"
                data-testid="group-name-input"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder="Ex: Equipe Fogo"
                className="mt-1"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    editingGroup ? handleUpdateGroup() : handleCreateGroup();
                  }
                }}
              />
            </div>

            <div>
              <Label className="text-sm font-bold text-slate-700">Cor do Grupo</Label>
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                {['#ef4444', '#f97316', '#eab308', '#22c55e', '#06b6d4', '#3b82f6', '#6366f1', '#a855f7', '#ec4899', '#64748b'].map((color) => (
                  <button
                    key={color}
                    data-testid={`group-color-${color}`}
                    onClick={() => setNewGroupColor(color)}
                    className={`w-8 h-8 rounded-full border-2 transition-all hover:scale-110 ${
                      newGroupColor === color ? 'border-slate-900 scale-110 ring-2 ring-offset-2 ring-slate-400' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
              <div className="mt-2 flex items-center gap-2">
                <div className="w-6 h-6 rounded-full border" style={{ backgroundColor: newGroupColor }} />
                <span className="text-xs text-slate-500 font-mono">{newGroupColor}</span>
              </div>
            </div>
          </div>

          <DialogFooter className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setCreateGroupDialogOpen(false);
                setEditingGroup(null);
                setNewGroupName('');
                setNewGroupColor('#6366f1');
              }}
            >
              Cancelar
            </Button>
            <Button
              data-testid="save-group-btn"
              onClick={editingGroup ? handleUpdateGroup : handleCreateGroup}
              disabled={!newGroupName.trim()}
              className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700"
            >
              {editingGroup ? 'Salvar Alterações' : 'Criar Grupo'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Group XP Dialog */}
      <Dialog open={groupXPDialogOpen} onOpenChange={(open) => {
        setGroupXPDialogOpen(open);
        if (!open) {
          setSelectedGroupForXP(null);
          setGroupXPAmount('');
          setGroupXPReason('');
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              AURA para {selectedGroupForXP?.name || 'Grupo'}
            </DialogTitle>
            <DialogDescription>
              Atribua AURA a todos os membros do grupo de uma vez.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {selectedGroupForXP && (
              <div className="bg-slate-50 rounded-xl p-3 flex items-center gap-3">
                <div className="w-5 h-5 rounded-full" style={{ backgroundColor: selectedGroupForXP.color }} />
                <span className="font-bold text-slate-800">{selectedGroupForXP.name}</span>
                <span className="text-xs bg-slate-200 px-2 py-0.5 rounded-full text-slate-600">
                  {selectedGroupForXP.members?.length || 0} membros
                </span>
              </div>
            )}
            <div>
              <Label htmlFor="group-xp-amount" className="text-sm font-bold text-slate-700">Quantidade de AURA</Label>
              <Input
                id="group-xp-amount"
                data-testid="group-xp-amount-input"
                type="number"
                min="1"
                value={groupXPAmount}
                onChange={(e) => setGroupXPAmount(e.target.value)}
                placeholder="Ex: 50"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="group-xp-reason" className="text-sm font-bold text-slate-700">Motivo (opcional)</Label>
              <Input
                id="group-xp-reason"
                data-testid="group-xp-reason-input"
                value={groupXPReason}
                onChange={(e) => setGroupXPReason(e.target.value)}
                placeholder="Ex: Trabalho em equipe"
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setGroupXPDialogOpen(false);
                setSelectedGroupForXP(null);
                setGroupXPAmount('');
                setGroupXPReason('');
              }}
              disabled={isApplyingGroupXP}
            >
              Cancelar
            </Button>
            <Button
              data-testid="apply-group-xp-btn"
              onClick={handleApplyGroupXP}
              disabled={isApplyingGroupXP || !groupXPAmount}
              className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700"
            >
              {isApplyingGroupXP ? (
                <span className="flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Atribuindo...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Atribuir AURA ao Grupo
                </span>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeacherClassroom;
